import React from 'react';
import { BRAND, localePath } from '../../../config/region';

/*
 * "vn-genres-guide" in every language it has been translated into.
 *
 * Shared fields (slug, dates) sit at the top level; each language supplies only what differs.
 * Russian is the authored original. A language that is missing here falls back to English, or to
 * Russian in the russia build — see blogPosts/index.js.
 */
const post = {
  slug: 'vn-genres-guide',
  datePublished: '2025-06-01',
  dateModified: '2026-03-01',

  russian: {
    title: '🎭 Жанры визуальных новелл: романтика, детектив, хоррор',
    description: 'Визуальные новеллы — многогранный жанр, вмещающий истории на любой вкус. Разберём основные жанры.',
    seoTitle: `Жанры визуальных новелл: романтика, детектив, хоррор — ${BRAND}`,
    seoDescription: 'Визуальные новеллы — многогранный жанр, вмещающий истории на любой вкус. От романтики до хоррора — разбираем основные жанры с примерами.',
    content: (
      <>
        <p>Визуальные новеллы — это многогранный жанр, который вмещает в себя истории на любой вкус. От трогательных романтических драм до леденящих душу хорроров — каждый найдёт что-то по душе. В этой статье разберём основные жанры визуальных новелл с примерами и особенностями.</p>

        <h2>Романтика</h2>

        <h3>Что это?</h3>

        <p>Романтические визуальные новеллы (Romance VN) — самый популярный жанр, где история сосредоточена на развитии любовных отношений.</p>

        <h3>Поджанры романтики</h3>

        <p><strong>Отомэ (Otome Game)</strong></p>
        <ul>
          <li>Для женской аудитории</li>
          <li>Главная героиня выбирает среди нескольких мужских персонажей</li>
          <li>Примеры: Mystic Messenger, Amnesia, Hakuoki</li>
        </ul>

        <p><strong>Галгэ (Galge/Bishoujo)</strong></p>
        <ul>
          <li>Для мужской аудитории</li>
          <li>Главный герой развивает отношения с женскими персонажами</li>
          <li>Примеры: Clannad, Katawa Shoujo, Doki Doki Literature Club</li>
        </ul>

        <p><strong>BL (Boys' Love/Yaoi)</strong></p>
        <ul>
          <li>Романтика между мужскими персонажами</li>
          <li>Примеры: Dramatical Murder, No, Thank You!!!</li>
        </ul>

        <p><strong>GL (Girls' Love/Yuri)</strong></p>
        <ul>
          <li>Романтика между женскими персонажами</li>
          <li>Примеры: Kindred Spirits on the Roof, Highway Blossoms</li>
        </ul>

        <h3>Особенности жанра</h3>

        <ul>
          <li><strong>Маршруты персонажей</strong> — отдельные сюжетные ветки для каждого романтического интереса</li>
          <li><strong>Система привязанности</strong> — выборы влияют на отношения</li>
          <li><strong>Множество концовок</strong> — от счастливых до трагических</li>
          <li><strong>Ключевые моменты</strong> — признания, первый поцелуй, конфликты</li>
        </ul>

        <h3>Архетипы персонажей</h3>

        <table>
          <thead>
            <tr>
              <th>Архетип</th>
              <th>Характеристика</th>
              <th>Пример</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Цундэрэ</td>
              <td>Холодный снаружи, нежный внутри</td>
              <td>Враждебность → привязанность</td>
            </tr>
            <tr>
              <td>Кудэрэ</td>
              <td>Спокойный, безэмоциональный</td>
              <td>Постепенное раскрытие чувств</td>
            </tr>
            <tr>
              <td>Яндэрэ</td>
              <td>Одержимая любовь</td>
              <td>Милый → опасный</td>
            </tr>
            <tr>
              <td>Дандэрэ</td>
              <td>Застенчивый, тихий</td>
              <td>Раскрывается постепенно</td>
            </tr>
            <tr>
              <td>Генки</td>
              <td>Энергичный, весёлый</td>
              <td>Заряжает позитивом</td>
            </tr>
          </tbody>
        </table>

        <h3>Романтика на {BRAND}</h3>

        <p>На платформе <a href={localePath('/')}>{BRAND}</a> вы можете создать собственную романтическую историю:</p>

        <ul>
          <li>Создайте персонажа с вашей личностью</li>
          <li>Развивайте отношения своими словами</li>
          <li>Каждый разговор уникален благодаря ИИ</li>
          <li>Флиртуйте, признавайтесь, ссорьтесь — как в жизни</li>
        </ul>

        <h2>Детектив и мистика</h2>

        <h3>Что это?</h3>

        <p>Детективные визуальные новеллы (Mystery VN) — истории, где игрок расследует преступления, разгадывает загадки или раскрывает тайны.</p>

        <h3>Поджанры</h3>

        <p><strong>Классический детектив</strong></p>
        <ul>
          <li>Расследование убийства</li>
          <li>Сбор улик и допросы</li>
          <li>Логические выводы</li>
          <li>Примеры: Ace Attorney, Danganronpa</li>
        </ul>

        <p><strong>Мистический триллер</strong></p>
        <ul>
          <li>Сверхъестественные элементы</li>
          <li>Паранормальные расследования</li>
          <li>Примеры: Umineko no Naku Koro ni, Higurashi</li>
        </ul>

        <p><strong>Психологический триллер</strong></p>
        <ul>
          <li>Игры разума</li>
          <li>Ненадёжный рассказчик</li>
          <li>Примеры: Ever17, Remember11</li>
        </ul>

        <p><strong>Нуар</strong></p>
        <ul>
          <li>Мрачная атмосфера</li>
          <li>Циничный протагонист</li>
          <li>Криминальный мир</li>
        </ul>

        <h3>Особенности жанра</h3>

        <ul>
          <li><strong>Система улик</strong> — игрок собирает доказательства</li>
          <li><strong>Допросы</strong> — противоречия в показаниях</li>
          <li><strong>Логические цепочки</strong> — соединение фактов</li>
          <li><strong>Твисты</strong> — неожиданные повороты</li>
          <li><strong>Несколько подозреваемых</strong> — кто настоящий преступник?</li>
        </ul>

        <h3>Элементы хорошего детектива</h3>

        <ol>
          <li><strong>Честная игра</strong> — все улики показаны игроку до разгадки</li>
          <li><strong>Мотивация злодея</strong> — понятные причины преступления</li>
          <li><strong>Ложные следы</strong> — отвлекающие маневры</li>
          <li><strong>Удовлетворительная разгадка</strong> — логичный и неожиданный ответ</li>
        </ol>

        <h3>Детективы на {BRAND}</h3>

        <p><a href={localePath('/')}>{BRAND}</a> позволяет расследовать уникальные дела:</p>

        <ul>
          <li>ИИ создаёт загадки, адаптируясь к вашим действиям</li>
          <li>Допрашивайте подозреваемых своими вопросами</li>
          <li>Каждое расследование уникально</li>
          <li>Станьте детективом в мире, где нет заготовленных ответов</li>
          <li>Попробуйте новеллу <a href={localePath('/production/_labirint_pamyati')}>«Лабиринт памяти»</a></li>
        </ul>

        <h2>Хоррор</h2>

        <h3>Что это?</h3>

        <p>Хоррор-новеллы (Horror VN) — истории, созданные чтобы пугать, вызывать тревогу и держать в напряжении.</p>

        <h3>Поджанры</h3>

        <p><strong>Психологический хоррор</strong></p>
        <ul>
          <li>Страх через атмосферу и неизвестность</li>
          <li>Игры с восприятием реальности</li>
          <li>Примеры: Doki Doki Literature Club, Saya no Uta</li>
        </ul>

        <p><strong>Сверхъестественный хоррор</strong></p>
        <ul>
          <li>Призраки, демоны, проклятия</li>
          <li>Японская мифология часто</li>
          <li>Примеры: Corpse Party, Higurashi</li>
        </ul>

        <p><strong>Survival Horror</strong></p>
        <ul>
          <li>Выживание в опасной ситуации</li>
          <li>Ограниченные ресурсы</li>
          <li>Монстры и преследователи</li>
        </ul>

        <p><strong>Готика</strong></p>
        <ul>
          <li>Мрачные замки и поместья</li>
          <li>Семейные тайны</li>
          <li>Атмосфера декаданса</li>
        </ul>

        <h3>Особенности жанра</h3>

        <ul>
          <li><strong>Атмосфера</strong> — музыка, визуал, текст создают тревогу</li>
          <li><strong>Jump scares</strong> — внезапные пугающие моменты</li>
          <li><strong>Медленное нарастание</strong> — напряжение растёт постепенно</li>
          <li><strong>Bad endings</strong> — смерть героя — частый исход</li>
          <li><strong>Тайны</strong> — что на самом деле происходит?</li>
        </ul>

        <h3>Техники создания страха</h3>

        <table>
          <thead>
            <tr>
              <th>Техника</th>
              <th>Описание</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Неизвестность</td>
              <td>Угроза не показана напрямую</td>
            </tr>
            <tr>
              <td>Изоляция</td>
              <td>Герой один, помощи нет</td>
            </tr>
            <tr>
              <td>Беспомощность</td>
              <td>Нельзя сражаться, только бежать</td>
            </tr>
            <tr>
              <td>Uncanny Valley</td>
              <td>Что-то знакомое, но «неправильное»</td>
            </tr>
            <tr>
              <td>Тишина</td>
              <td>Пауза перед ужасом</td>
            </tr>
            <tr>
              <td>Обман ожиданий</td>
              <td>Безопасное оказывается опасным</td>
            </tr>
          </tbody>
        </table>

        <h3>Хоррор на {BRAND}</h3>

        <p>На <a href={localePath('/')}>{BRAND}</a> хоррор-истории особенно эффективны:</p>

        <ul>
          <li>ИИ непредсказуем — вы не знаете, что будет дальше</li>
          <li>Персонажи реагируют на ваш страх</li>
          <li>Каждое прохождение — новые ужасы</li>
          <li>Настоящее ощущение неизвестности</li>
          <li>Попробуйте новеллу <a href={localePath('/production/lastnightgame')}>«Последняя Ночь»</a></li>
        </ul>

        <h2>Фэнтези</h2>

        <h3>Что это?</h3>

        <p>Фэнтези-новеллы переносят игрока в миры магии, мечей и эпических приключений.</p>

        <h3>Поджанры</h3>

        <ul>
          <li><strong>Классическое фэнтези</strong> — эльфы, драконы, квесты</li>
          <li><strong>Тёмное фэнтези</strong> — мрачный мир, моральная серость</li>
          <li><strong>Городское фэнтези</strong> — магия в современном мире</li>
          <li><strong>Восточное фэнтези</strong> — азиатская мифология</li>
          <li><strong>LitRPG</strong> — игровые механики в сюжете</li>
        </ul>

        <h3>Особенности жанра</h3>

        <ul>
          <li><strong>Магическая система</strong> — как работает волшебство</li>
          <li><strong>Worldbuilding</strong> — продуманный мир</li>
          <li><strong>Эпический масштаб</strong> — судьба мира на кону</li>
          <li><strong>Приключения</strong> — путешествия и сражения</li>
        </ul>

        <h2>Повседневность (Slice of Life)</h2>

        <h3>Что это?</h3>

        <p>Slice of Life — истории о обычной жизни без глобальных конфликтов.</p>

        <h3>Особенности</h3>

        <ul>
          <li>Школа, работа, дружба</li>
          <li>Нет антагониста или катастрофы</li>
          <li>Фокус на отношениях между людьми</li>
          <li>Юмор и трогательные моменты</li>
          <li>Примеры: Бесконечное лето, Clannad (частично)</li>
        </ul>

        <h2>Научная фантастика</h2>

        <h3>Что это?</h3>

        <p>Sci-Fi новеллы исследуют влияние технологий на общество и человека.</p>

        <h3>Поджанры</h3>

        <ul>
          <li><strong>Киберпанк</strong> — высокие технологии, низкая жизнь</li>
          <li><strong>Космоопера</strong> — приключения в космосе</li>
          <li><strong>Постапокалипсис</strong> — мир после катастрофы</li>
          <li><strong>Временные петли</strong> — путешествия во времени</li>
        </ul>

        <h3>Примеры</h3>

        <ul>
          <li>Steins;Gate — путешествия во времени</li>
          <li>AI: The Somnium Files — технологии сновидений</li>
          <li>Planetarian — робот и конец света</li>
        </ul>

        <h2>Смешанные жанры</h2>

        <p>Лучшие новеллы часто комбинируют жанры:</p>

        <table>
          <thead>
            <tr>
              <th>Новелла</th>
              <th>Жанры</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Doki Doki Literature Club</td>
              <td>Романтика + Психологический хоррор</td>
            </tr>
            <tr>
              <td>Danganronpa</td>
              <td>Детектив + Хоррор + Триллер</td>
            </tr>
            <tr>
              <td>Steins;Gate</td>
              <td>Sci-Fi + Триллер + Романтика</td>
            </tr>
            <tr>
              <td>Fate/stay night</td>
              <td>Фэнтези + Экшен + Романтика</td>
            </tr>
            <tr>
              <td>Umineko</td>
              <td>Детектив + Мистика + Фэнтези</td>
            </tr>
          </tbody>
        </table>

        <h2>Как выбрать жанр для себя</h2>

        <h3>Тест: Какой жанр вам подходит?</h3>

        <p><strong>Что для вас важнее в истории?</strong></p>
        <ul>
          <li>Отношения между персонажами → <strong>Романтика</strong></li>
          <li>Загадки и логика → <strong>Детектив</strong></li>
          <li>Эмоции и атмосфера → <strong>Хоррор</strong> или <strong>Драма</strong></li>
          <li>Приключения и экшен → <strong>Фэнтези</strong></li>
          <li>Идеи и концепции → <strong>Sci-Fi</strong></li>
        </ul>

        <h3>Попробуйте всё на {BRAND}</h3>

        <p>Платформа <a href={localePath('/')}>{BRAND}</a> предлагает новеллы всех жанров:</p>

        <ul>
          <li><strong>Романтика</strong> — создайте идеальную историю любви</li>
          <li><strong>Детектив</strong> — расследуйте уникальные дела</li>
          <li><strong>Хоррор</strong> — испытайте настоящий страх неизвестности</li>
          <li><strong>Фэнтези</strong> — станьте героем магического мира</li>
        </ul>

        <p>И главное — каждая история уникальна благодаря ИИ.</p>

        <h2>Заключение</h2>

        <p>Визуальные новеллы охватывают все возможные жанры — от светлой романтики до мрачного хоррора. Какой бы тип историй вы ни любили, для вас найдётся идеальная новелла.</p>

        <p>А если хотите попробовать все жанры с максимальной свободой — <a href={localePath('/')}>{BRAND}</a> ждёт вас. Создайте персонажа, выберите мир и проживите историю, которая будет только вашей.</p>

        <p><strong>Какой жанр выберете вы?</strong></p>
      </>
    ),
  },

  english: {
    title: '🎭 Visual Novel Genres: Romance, Mystery, Horror',
    description: "Visual novels are a multifaceted genre with stories for every taste. Let's explore the main genres.",
seoTitle: `Visual Novel Genres: Romance, Mystery, Horror — ${BRAND}`,
    seoDescription: 'Visual novels are a multifaceted genre with stories for every taste. From touching romance to chilling horror — exploring the main genres with examples.',
    content: (
      <>
        <p>Visual novels are a multifaceted genre that contains stories for every taste. From touching romantic dramas to spine-chilling horrors — everyone will find something they like. In this article, we&apos;ll explore the main visual novel genres with examples and features.</p>

        <h2>Romance</h2>

        <h3>What is it?</h3>

        <p>Romantic visual novels (Romance VN) — the most popular genre, where the story focuses on the development of romantic relationships.</p>

        <h3>Romance subgenres</h3>

        <p><strong>Otome (Otome Game)</strong></p>
        <ul>
          <li>For female audience</li>
          <li>Female protagonist chooses among several male characters</li>
          <li>Examples: Mystic Messenger, Amnesia, Hakuoki</li>
        </ul>

        <p><strong>Galge (Galge/Bishoujo)</strong></p>
        <ul>
          <li>For male audience</li>
          <li>Male protagonist develops relationships with female characters</li>
          <li>Examples: Clannad, Katawa Shoujo, Doki Doki Literature Club</li>
        </ul>

        <p><strong>BL (Boys&apos; Love/Yaoi)</strong></p>
        <ul>
          <li>Romance between male characters</li>
          <li>Examples: Dramatical Murder, No, Thank You!!!</li>
        </ul>

        <p><strong>GL (Girls&apos; Love/Yuri)</strong></p>
        <ul>
          <li>Romance between female characters</li>
          <li>Examples: Kindred Spirits on the Roof, Highway Blossoms</li>
        </ul>

        <h3>Genre features</h3>

        <ul>
          <li><strong>Character routes</strong> — separate story branches for each romantic interest</li>
          <li><strong>Affection system</strong> — choices affect relationships</li>
          <li><strong>Multiple endings</strong> — from happy to tragic</li>
          <li><strong>Key moments</strong> — confessions, first kiss, conflicts</li>
        </ul>

        <h3>Character archetypes</h3>

        <table>
          <thead>
            <tr>
              <th>Archetype</th>
              <th>Characteristics</th>
              <th>Example</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Tsundere</td>
              <td>Cold outside, tender inside</td>
              <td>Hostility → affection</td>
            </tr>
            <tr>
              <td>Kuudere</td>
              <td>Calm, emotionless</td>
              <td>Gradual emotional opening</td>
            </tr>
            <tr>
              <td>Yandere</td>
              <td>Obsessive love</td>
              <td>Sweet → dangerous</td>
            </tr>
            <tr>
              <td>Dandere</td>
              <td>Shy, quiet</td>
              <td>Gradually opens up</td>
            </tr>
            <tr>
              <td>Genki</td>
              <td>Energetic, cheerful</td>
              <td>Radiates positivity</td>
            </tr>
          </tbody>
        </table>

        <h3>Romance on {BRAND}</h3>

        <p>On the platform <a href={localePath('/')}>{BRAND}</a> you can create your own romantic story:</p>

        <ul>
          <li>Create a character with your personality</li>
          <li>Develop relationships in your own words</li>
          <li>Every conversation is unique thanks to AI</li>
          <li>Flirt, confess, argue — just like in real life</li>
        </ul>

        <h2>Mystery and Mysticism</h2>

        <h3>What is it?</h3>

        <p>Mystery visual novels (Mystery VN) — stories where the player investigates crimes, solves puzzles, or uncovers secrets.</p>

        <h3>Subgenres</h3>

        <p><strong>Classic detective</strong></p>
        <ul>
          <li>Murder investigation</li>
          <li>Evidence gathering and interrogations</li>
          <li>Logical deductions</li>
          <li>Examples: Ace Attorney, Danganronpa</li>
        </ul>

        <p><strong>Mystical thriller</strong></p>
        <ul>
          <li>Supernatural elements</li>
          <li>Paranormal investigations</li>
          <li>Examples: Umineko no Naku Koro ni, Higurashi</li>
        </ul>

        <p><strong>Psychological thriller</strong></p>
        <ul>
          <li>Mind games</li>
          <li>Unreliable narrator</li>
          <li>Examples: Ever17, Remember11</li>
        </ul>

        <p><strong>Noir</strong></p>
        <ul>
          <li>Dark atmosphere</li>
          <li>Cynical protagonist</li>
          <li>Criminal underworld</li>
        </ul>

        <h3>Genre features</h3>

        <ul>
          <li><strong>Evidence system</strong> — player gathers proof</li>
          <li><strong>Interrogations</strong> — contradictions in testimony</li>
          <li><strong>Logic chains</strong> — connecting facts</li>
          <li><strong>Twists</strong> — unexpected turns</li>
          <li><strong>Multiple suspects</strong> — who is the real criminal?</li>
        </ul>

        <h3>Elements of a good mystery</h3>

        <ol>
          <li><strong>Fair play</strong> — all clues are shown to the player before the solution</li>
          <li><strong>Villain motivation</strong> — understandable reasons for the crime</li>
          <li><strong>Red herrings</strong> — distracting maneuvers</li>
          <li><strong>Satisfying resolution</strong> — logical and unexpected answer</li>
        </ol>

        <h3>Mysteries on {BRAND}</h3>

        <p><a href={localePath('/')}>{BRAND}</a> allows you to investigate unique cases:</p>

        <ul>
          <li>AI creates puzzles, adapting to your actions</li>
          <li>Interrogate suspects with your own questions</li>
          <li>Each investigation is unique</li>
          <li>Become a detective in a world with no predetermined answers</li>
          <li>Try the novel <a href={localePath('/production/labyrinth-of-memory')}>Labyrinth of Memory</a></li>
        </ul>

        <h2>Horror</h2>

        <h3>What is it?</h3>

        <p>Horror novels (Horror VN) — stories created to frighten, cause anxiety, and keep you on edge.</p>

        <h3>Subgenres</h3>

        <p><strong>Psychological horror</strong></p>
        <ul>
          <li>Fear through atmosphere and the unknown</li>
          <li>Games with perception of reality</li>
          <li>Examples: Doki Doki Literature Club, Saya no Uta</li>
        </ul>

        <p><strong>Supernatural horror</strong></p>
        <ul>
          <li>Ghosts, demons, curses</li>
          <li>Japanese mythology often features</li>
          <li>Examples: Corpse Party, Higurashi</li>
        </ul>

        <p><strong>Survival Horror</strong></p>
        <ul>
          <li>Survival in dangerous situations</li>
          <li>Limited resources</li>
          <li>Monsters and pursuers</li>
        </ul>

        <p><strong>Gothic</strong></p>
        <ul>
          <li>Dark castles and estates</li>
          <li>Family secrets</li>
          <li>Atmosphere of decay</li>
        </ul>

        <h3>Genre features</h3>

        <ul>
          <li><strong>Atmosphere</strong> — music, visuals, text create anxiety</li>
          <li><strong>Jump scares</strong> — sudden frightening moments</li>
          <li><strong>Slow build-up</strong> — tension grows gradually</li>
          <li><strong>Bad endings</strong> — hero death is a common outcome</li>
          <li><strong>Mysteries</strong> — what&apos;s really happening?</li>
        </ul>

        <h3>Techniques for creating fear</h3>

        <table>
          <thead>
            <tr>
              <th>Technique</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>The unknown</td>
              <td>Threat is not shown directly</td>
            </tr>
            <tr>
              <td>Isolation</td>
              <td>Hero is alone, no help</td>
            </tr>
            <tr>
              <td>Helplessness</td>
              <td>Can only run, cannot fight</td>
            </tr>
            <tr>
              <td>Uncanny Valley</td>
              <td>Something familiar but &quot;wrong&quot;</td>
            </tr>
            <tr>
              <td>Silence</td>
              <td>Pause before horror</td>
            </tr>
            <tr>
              <td>Subverted expectations</td>
              <td>Safe becomes dangerous</td>
            </tr>
          </tbody>
        </table>

        <h3>Horror on {BRAND}</h3>

        <p>On <a href={localePath('/')}>{BRAND}</a> horror stories are especially effective:</p>

        <ul>
          <li>AI is unpredictable — you do not know what comes next</li>
          <li>Characters react to your fear</li>
          <li>Each playthrough brings new horrors</li>
          <li>True sense of the unknown</li>
          <li>Try the novel <a href={localePath('/production/last-night-game')}>Last Night Game</a></li>
        </ul>

        <h2>Fantasy</h2>

        <h3>What is it?</h3>

        <p>Fantasy novels transport the player to worlds of magic, swords, and epic adventures.</p>

        <h3>Subgenres</h3>

        <ul>
          <li><strong>Classic fantasy</strong> — elves, dragons, quests</li>
          <li><strong>Dark fantasy</strong> — grim world, moral ambiguity</li>
          <li><strong>Urban fantasy</strong> — magic in the modern world</li>
          <li><strong>Eastern fantasy</strong> — Asian mythology</li>
          <li><strong>LitRPG</strong> — game mechanics in the plot</li>
        </ul>

        <h3>Genre features</h3>

        <ul>
          <li><strong>Magic system</strong> — how magic works</li>
          <li><strong>Worldbuilding</strong> — a thoughtfully crafted world</li>
          <li><strong>Epic scale</strong> — the fate of the world at stake</li>
          <li><strong>Adventures</strong> — journeys and battles</li>
        </ul>

        <h2>Slice of Life</h2>

        <h3>What is it?</h3>

        <p>Slice of Life — stories about ordinary life without global conflicts.</p>

        <h3>Features</h3>

        <ul>
          <li>School, work, friendship</li>
          <li>No antagonist or catastrophe</li>
          <li>Focus on relationships between people</li>
          <li>Humor and touching moments</li>
          <li>Examples: Endless Summer, Clannad (partially)</li>
        </ul>

        <h2>Science Fiction</h2>

        <h3>What is it?</h3>

        <p>Sci-Fi novels explore the impact of technology on society and humanity.</p>

        <h3>Subgenres</h3>

        <ul>
          <li><strong>Cyberpunk</strong> — high tech, low life</li>
          <li><strong>Space opera</strong> — adventures in space</li>
          <li><strong>Post-apocalypse</strong> — world after catastrophe</li>
          <li><strong>Time loops</strong> — time travel</li>
        </ul>

        <h3>Examples</h3>

        <ul>
          <li>Steins;Gate — time travel</li>
          <li>AI: The Somnium Files — dream technology</li>
          <li>Planetarian — robot and the end of the world</li>
        </ul>

        <h2>Mixed genres</h2>

        <p>The best novels often combine genres:</p>

        <table>
          <thead>
            <tr>
              <th>Novel</th>
              <th>Genres</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Doki Doki Literature Club</td>
              <td>Romance + Psychological horror</td>
            </tr>
            <tr>
              <td>Danganronpa</td>
              <td>Mystery + Horror + Thriller</td>
            </tr>
            <tr>
              <td>Steins;Gate</td>
              <td>Sci-Fi + Thriller + Romance</td>
            </tr>
            <tr>
              <td>Fate/stay night</td>
              <td>Fantasy + Action + Romance</td>
            </tr>
            <tr>
              <td>Umineko</td>
              <td>Mystery + Mysticism + Fantasy</td>
            </tr>
          </tbody>
        </table>

        <h2>How to choose a genre for yourself</h2>

        <h3>Quiz: Which genre suits you?</h3>

        <p><strong>What matters most to you in a story?</strong></p>
        <ul>
          <li>Relationships between characters → <strong>Romance</strong></li>
          <li>Puzzles and logic → <strong>Mystery</strong></li>
          <li>Emotions and atmosphere → <strong>Horror</strong> or <strong>Drama</strong></li>
          <li>Adventures and action → <strong>Fantasy</strong></li>
          <li>Ideas and concepts → <strong>Sci-Fi</strong></li>
        </ul>

        <h3>Try everything on {BRAND}</h3>

        <p>The platform <a href={localePath('/')}>{BRAND}</a> offers novels of all genres:</p>

        <ul>
          <li><strong>Romance</strong> — create your ideal love story</li>
          <li><strong>Mystery</strong> — investigate unique cases</li>
          <li><strong>Horror</strong> — experience true fear of the unknown</li>
          <li><strong>Fantasy</strong> — become a hero of a magical world</li>
        </ul>

        <p>And most importantly — every story is unique thanks to AI.</p>

        <h2>Conclusion</h2>

        <p>Visual novels cover all possible genres — from light romance to dark horror. Whatever type of stories you like, you will find the perfect novel.</p>

        <p>And if you want to try all genres with maximum freedom — <a href={localePath('/')}>{BRAND}</a> awaits you. Create a character, choose a world, and live a story that will be only yours.</p>

        <p><strong>Which genre will you choose?</strong></p>
      </>
    ),
  },

  german: {
    title: '🎭 Visual Novel Genres: Romantik, Mystery, Horror',
    description: 'Visual Novels sind ein facettenreiches Genre mit Geschichten für jeden Geschmack. Erkunden wir die wichtigsten Genres.',
seoTitle: `Visual Novel Genres: Romantik, Mystery, Horror — ${BRAND}`,
    seoDescription: 'Visual Novels sind ein facettenreiches Genre mit Geschichten für jeden Geschmack. Von ergreifender Romantik bis zu fesselndem Horror — Erforschung der wichtigsten Genres mit Beispielen.',
    content: (
      <>
        <p>Visual Novels sind ein facettenreiches Genre, das Geschichten für jeden Geschmack enthält. Von ergreifenden romantischen Dramen bis zu erschreckenden Horrorgeschichten — jeder wird etwas finden, das ihm gefällt. In diesem Artikel werden wir die Hauptgenres von Visual Novels mit Beispielen und Besonderheiten erkunden.</p>

        <h2>Romantik</h2>

        <h3>Was ist das?</h3>

        <p>Romantische Visual Novels (Romance VN) — das beliebteste Genre, in dem die Geschichte auf der Entwicklung von romantischen Beziehungen konzentriert ist.</p>

        <h3>Romantik-Subgenres</h3>

        <p><strong>Otome (Otome Game)</strong></p>
        <ul>
          <li>Für weibliches Publikum</li>
          <li>Weibliche Hauptfigur wählt zwischen mehreren männlichen Charakteren</li>
          <li>Beispiele: Mystic Messenger, Amnesia, Hakuoki</li>
        </ul>

        <p><strong>Galge (Galge/Bishoujo)</strong></p>
        <ul>
          <li>Für männliches Publikum</li>
          <li>Männliche Hauptfigur entwickelt Beziehungen zu weiblichen Charakteren</li>
          <li>Beispiele: Clannad, Katawa Shoujo, Doki Doki Literature Club</li>
        </ul>

        <p><strong>BL (Boys&apos; Love/Yaoi)</strong></p>
        <ul>
          <li>Romantik zwischen männlichen Charakteren</li>
          <li>Beispiele: Dramatical Murder, No, Thank You!!!</li>
        </ul>

        <p><strong>GL (Girls&apos; Love/Yuri)</strong></p>
        <ul>
          <li>Romantik zwischen weiblichen Charakteren</li>
          <li>Beispiele: Kindred Spirits on the Roof, Highway Blossoms</li>
        </ul>

        <h3>Genremerkmale</h3>

        <ul>
          <li><strong>Charakterrouten</strong> — separate Handlungszweige für jeden romantischen Schwerpunkt</li>
          <li><strong>Zuneigungssystem</strong> — Entscheidungen beeinflussen Beziehungen</li>
          <li><strong>Mehrfache Enden</strong> — von glücklich bis tragisch</li>
          <li><strong>Schlüsselmomente</strong> — Geständnisse, erster Kuss, Konflikte</li>
        </ul>

        <h3>Charakterarchetypen</h3>

        <table>
          <thead>
            <tr>
              <th>Archetyp</th>
              <th>Charakteristiken</th>
              <th>Beispiel</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Tsundere</td>
              <td>Kalt außen, zärtlich innen</td>
              <td>Feindseligkeit → Zuneigung</td>
            </tr>
            <tr>
              <td>Kuudere</td>
              <td>Ruhig, emotionslos</td>
              <td>Allmähliche emotionale Öffnung</td>
            </tr>
            <tr>
              <td>Yandere</td>
              <td>Obsessive Liebe</td>
              <td>Süß → gefährlich</td>
            </tr>
            <tr>
              <td>Dandere</td>
              <td>Schüchtern, leise</td>
              <td>Öffnet sich allmählich</td>
            </tr>
            <tr>
              <td>Genki</td>
              <td>Energisch, fröhlich</td>
              <td>Strahlt Positivität aus</td>
            </tr>
          </tbody>
        </table>

        <h3>Romantik auf {BRAND}</h3>

        <p>Auf der Plattform <a href={localePath('/')}>{BRAND}</a> kannst du deine eigene romantische Geschichte erstellen:</p>

        <ul>
          <li>Erstelle einen Charakter mit deiner Persönlichkeit</li>
          <li>Entwickle Beziehungen in deinen eigenen Worten</li>
          <li>Jedes Gespräch ist einzigartig dank KI</li>
          <li>Flirte, gestehe, streite — wie im echten Leben</li>
        </ul>

        <h2>Mystery und Mystik</h2>

        <h3>Was ist das?</h3>

        <p>Mystery Visual Novels (Mystery VN) — Geschichten, in denen der Spieler Verbrechen untersucht, Rätsel löst oder Geheimnisse aufdeckt.</p>

        <h3>Subgenres</h3>

        <p><strong>Klassischer Krimi</strong></p>
        <ul>
          <li>Mordermittlung</li>
          <li>Beweissammlung und Verhöre</li>
          <li>Logische Schlussfolgerungen</li>
          <li>Beispiele: Ace Attorney, Danganronpa</li>
        </ul>

        <p><strong>Mystischer Thriller</strong></p>
        <ul>
          <li>Übernatürliche Elemente</li>
          <li>Paranormale Untersuchungen</li>
          <li>Beispiele: Umineko no Naku Koro ni, Higurashi</li>
        </ul>

        <p><strong>Psychologischer Thriller</strong></p>
        <ul>
          <li>Gedankenspiele</li>
          <li>Unzuverlässiger Erzähler</li>
          <li>Beispiele: Ever17, Remember11</li>
        </ul>

        <p><strong>Noir</strong></p>
        <ul>
          <li>Dunkle Atmosphäre</li>
          <li>Zynischer Protagonist</li>
          <li>Kriminelle Unterwelt</li>
        </ul>

        <h3>Genremerkmale</h3>

        <ul>
          <li><strong>Beweissystem</strong> — Der Spieler sammelt Beweise</li>
          <li><strong>Verhöre</strong> — Widersprüche in den Aussagen</li>
          <li><strong>Logische Ketten</strong> — Fakten verbinden</li>
          <li><strong>Twists</strong> — unerwartete Wendungen</li>
          <li><strong>Mehrere Verdächtige</strong> — Wer ist der echte Verbrecher?</li>
        </ul>

        <h3>Elemente eines guten Mysteries</h3>

        <ol>
          <li><strong>Fairer Spielverlauf</strong> — alle Hinweise werden dem Spieler vor der Lösung gezeigt</li>
          <li><strong>Bösewicht-Motivation</strong> — verständliche Gründe für das Verbrechen</li>
          <li><strong>Falsche Fährten</strong> — ablenkende Manöver</li>
          <li><strong>Befriedigende Auflösung</strong> — logische und unerwartete Antwort</li>
        </ol>

        <h3>Mysteries auf {BRAND}</h3>

        <p><a href={localePath('/')}>{BRAND}</a> erlaubt dir, einzigartige Fälle zu untersuchen:</p>

        <ul>
          <li>KI erstellt Rätsel und passt sich deinen Handlungen an</li>
          <li>Verhöre Verdächtige mit deinen eigenen Fragen</li>
          <li>Jede Untersuchung ist einzigartig</li>
          <li>Werde ein Detektiv in einer Welt ohne vorgefertigte Antworten</li>
          <li>Versuche den Roman <a href={localePath('/production/labyrinth-of-memory')}>Labyrinth der Erinnerung</a></li>
        </ul>

        <h2>Horror</h2>

        <h3>Was ist das?</h3>

        <p>Horror-Novellen (Horror VN) — Geschichten, die geschaffen wurden, um zu erschrecken, Angst zu verursachen und dich in Spannung zu halten.</p>

        <h3>Subgenres</h3>

        <p><strong>Psychologischer Horror</strong></p>
        <ul>
          <li>Angst durch Atmosphäre und das Unbekannte</li>
          <li>Spiele mit der Wahrnehmung der Realität</li>
          <li>Beispiele: Doki Doki Literature Club, Saya no Uta</li>
        </ul>

        <p><strong>Übernatürlicher Horror</strong></p>
        <ul>
          <li>Gespenster, Dämonen, Flüche</li>
          <li>Japanische Mythologie ist oft vorhanden</li>
          <li>Beispiele: Corpse Party, Higurashi</li>
        </ul>

        <p><strong>Survival Horror</strong></p>
        <ul>
          <li>Überleben in gefährlichen Situationen</li>
          <li>Begrenzte Ressourcen</li>
          <li>Monster und Verfolger</li>
        </ul>

        <p><strong>Gotik</strong></p>
        <ul>
          <li>Dunkle Schlösser und Herrenhäuser</li>
          <li>Familiengeheimnisse</li>
          <li>Atmosphäre des Verfalls</li>
        </ul>

        <h3>Genremerkmale</h3>

        <ul>
          <li><strong>Atmosphäre</strong> — Musik, Bilder, Text schaffen Angst</li>
          <li><strong>Jump Scares</strong> — plötzliche erschreckende Momente</li>
          <li><strong>Langsames Aufbau</strong> — Spannung wächst allmählich</li>
          <li><strong>Böse Enden</strong> — Heldentod ist ein häufiger Ausgang</li>
          <li><strong>Mysterien</strong> — Was passiert wirklich?</li>
        </ul>

        <h3>Techniken zur Angstschaffung</h3>

        <table>
          <thead>
            <tr>
              <th>Technik</th>
              <th>Beschreibung</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Das Unbekannte</td>
              <td>Bedrohung wird nicht direkt gezeigt</td>
            </tr>
            <tr>
              <td>Isolation</td>
              <td>Held ist allein, keine Hilfe</td>
            </tr>
            <tr>
              <td>Hilflosigkeit</td>
              <td>Kann nur fliehen, nicht kämpfen</td>
            </tr>
            <tr>
              <td>Uncanny Valley</td>
              <td>Etwas Vertrautes, aber &quot;falsch&quot;</td>
            </tr>
            <tr>
              <td>Stille</td>
              <td>Pause vor dem Horror</td>
            </tr>
            <tr>
              <td>Unterbrochene Erwartungen</td>
              <td>Sicher wird gefährlich</td>
            </tr>
          </tbody>
        </table>

        <h3>Horror auf {BRAND}</h3>

        <p>Auf <a href={localePath('/')}>{BRAND}</a> sind Horror-Geschichten besonders effektiv:</p>

        <ul>
          <li>KI ist unvorhersehbar — du weißt nicht, was als nächstes kommt</li>
          <li>Charaktere reagieren auf deine Angst</li>
          <li>Jeder Durchgang bringt neue Schrecken</li>
          <li>Echtes Gefühl des Unbekannten</li>
          <li>Versuche den Roman <a href={localePath('/production/last-night-game')}>Letzte Nacht</a></li>
        </ul>

        <h2>Fantasy</h2>

        <h3>Was ist das?</h3>

        <p>Fantasy-Novellen bringen den Spieler in Welten der Magie, Schwerter und epischer Abenteuer.</p>

        <h3>Subgenres</h3>

        <ul>
          <li><strong>Klassische Fantasy</strong> — Elfen, Drachen, Quests</li>
          <li><strong>Dunkle Fantasy</strong> — düstere Welt, moralische Mehrdeutigkeit</li>
          <li><strong>Urban Fantasy</strong> — Magie in der modernen Welt</li>
          <li><strong>Östliche Fantasy</strong> — Asiatische Mythologie</li>
          <li><strong>LitRPG</strong> — Spielmechaniken in der Handlung</li>
        </ul>

        <h3>Genremerkmale</h3>

        <ul>
          <li><strong>Magiesystem</strong> — wie Magie funktioniert</li>
          <li><strong>Weltenbau</strong> — eine durchdacht gestaltete Welt</li>
          <li><strong>Epischer Umfang</strong> — das Schicksal der Welt steht auf dem Spiel</li>
          <li><strong>Abenteuer</strong> — Reisen und Kämpfe</li>
        </ul>

        <h2>Slice of Life</h2>

        <h3>Was ist das?</h3>

        <p>Slice of Life — Geschichten über das alltägliche Leben ohne globale Konflikte.</p>

        <h3>Merkmale</h3>

        <ul>
          <li>Schule, Arbeit, Freundschaft</li>
          <li>Kein Gegner oder Katastrophe</li>
          <li>Fokus auf Beziehungen zwischen Menschen</li>
          <li>Humor und ergreifende Momente</li>
          <li>Beispiele: Endloser Sommer, Clannad (teilweise)</li>
        </ul>

        <h2>Science Fiction</h2>

        <h3>Was ist das?</h3>

        <p>Sci-Fi-Novellen erforschen die Auswirkungen von Technologie auf Gesellschaft und Menschheit.</p>

        <h3>Subgenres</h3>

        <ul>
          <li><strong>Cyberpunk</strong> — hohe Technik, niedriges Leben</li>
          <li><strong>Weltraum-Oper</strong> — Abenteuer im Weltall</li>
          <li><strong>Post-Apokalypse</strong> — Welt nach der Katastrophe</li>
          <li><strong>Zeitschleifen</strong> — Zeitreisen</li>
        </ul>

        <h3>Beispiele</h3>

        <ul>
          <li>Steins;Gate — Zeitreisen</li>
          <li>AI: The Somnium Files — Traumtechnologie</li>
          <li>Planetarian — Roboter und das Ende der Welt</li>
        </ul>

        <h2>Gemischte Genres</h2>

        <p>Die besten Novellen kombinieren oft Genres:</p>

        <table>
          <thead>
            <tr>
              <th>Novelle</th>
              <th>Genres</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Doki Doki Literature Club</td>
              <td>Romantik + Psychologischer Horror</td>
            </tr>
            <tr>
              <td>Danganronpa</td>
              <td>Mystery + Horror + Thriller</td>
            </tr>
            <tr>
              <td>Steins;Gate</td>
              <td>Sci-Fi + Thriller + Romantik</td>
            </tr>
            <tr>
              <td>Fate/stay night</td>
              <td>Fantasy + Action + Romantik</td>
            </tr>
            <tr>
              <td>Umineko</td>
              <td>Mystery + Mystik + Fantasy</td>
            </tr>
          </tbody>
        </table>

        <h2>Wie du das richtige Genre für dich wählst</h2>

        <h3>Quiz: Welches Genre passt zu dir?</h3>

        <p><strong>Was ist dir in einer Geschichte am wichtigsten?</strong></p>
        <ul>
          <li>Beziehungen zwischen Charakteren → <strong>Romantik</strong></li>
          <li>Rätsel und Logik → <strong>Mystery</strong></li>
          <li>Emotionen und Atmosphäre → <strong>Horror</strong> oder <strong>Drama</strong></li>
          <li>Abenteuer und Action → <strong>Fantasy</strong></li>
          <li>Ideen und Konzepte → <strong>Sci-Fi</strong></li>
        </ul>

        <h3>Probiere alles auf {BRAND} aus</h3>

        <p>Die Plattform <a href={localePath('/')}>{BRAND}</a> bietet Novellen aller Genres:</p>

        <ul>
          <li><strong>Romantik</strong> — erstelle deine ideale Liebesgeschichte</li>
          <li><strong>Mystery</strong> — untersuche einzigartige Fälle</li>
          <li><strong>Horror</strong> — erlebe echte Angst vor dem Unbekannten</li>
          <li><strong>Fantasy</strong> — werde ein Held einer magischen Welt</li>
        </ul>

        <p>Und das Wichtigste — jede Geschichte ist einzigartig dank KI.</p>

        <h2>Fazit</h2>

        <p>Visual Novels decken alle möglichen Genres ab — von leichter Romantik bis dunkelm Horror. Was auch immer für Geschichten du magst, du wirst die perfekte Novelle finden.</p>

        <p>Und wenn du alle Genres mit maximaler Freiheit ausprobieren möchtest — <a href={localePath('/')}>{BRAND}</a> wartet auf dich. Erstelle einen Charakter, wähle eine Welt und erlebe eine Geschichte, die nur dir gehört.</p>

        <p><strong>Welches Genre wählst du?</strong></p>
      </>
    ),
  },

  french: {
    title: '🎭 Genres des romans visuels : romance, mystère, horreur',
    description: 'Les romans visuels sont un genre aux facettes multiples avec des histoires pour tous les goûts. Explorons les genres principaux.',
seoTitle: `Genres des romans visuels : romance, mystère, horreur — ${BRAND}`,
    seoDescription: "Les romans visuels sont un genre aux facettes multiples avec des histoires pour tous les goûts. De la romance touchante à l'horreur glaçante — exploration des genres principaux avec exemples.",
    content: (
      <>
        <p>Les romans visuels sont un genre aux facettes multiples qui contient des histoires pour tous les goûts. Des drames romantiques touchants aux horreurs glaçantes — chacun trouvera quelque chose qui lui plaît. Dans cet article, nous explorerons les principaux genres des romans visuels avec des exemples et des caractéristiques.</p>

        <h2>Romance</h2>

        <h3>Qu&apos;est-ce que c&apos;est ?</h3>

        <p>Les romans visuels romantiques (Romance VN) — le genre le plus populaire, où l&apos;histoire se concentre sur le développement des relations amoureuses.</p>

        <h3>Sous-genres de la romance</h3>

        <p><strong>Otome (Otome Game)</strong></p>
        <ul>
          <li>Pour le public féminin</li>
          <li>L&apos;héroïne principale choisit parmi plusieurs personnages masculins</li>
          <li>Exemples : Mystic Messenger, Amnesia, Hakuoki</li>
        </ul>

        <p><strong>Galge (Galge/Bishoujo)</strong></p>
        <ul>
          <li>Pour le public masculin</li>
          <li>Le héros principal développe des relations avec des personnages féminins</li>
          <li>Exemples : Clannad, Katawa Shoujo, Doki Doki Literature Club</li>
        </ul>

        <p><strong>BL (Boys&apos; Love/Yaoi)</strong></p>
        <ul>
          <li>Romance entre personnages masculins</li>
          <li>Exemples : Dramatical Murder, No, Thank You!!!</li>
        </ul>

        <p><strong>GL (Girls&apos; Love/Yuri)</strong></p>
        <ul>
          <li>Romance entre personnages féminins</li>
          <li>Exemples : Kindred Spirits on the Roof, Highway Blossoms</li>
        </ul>

        <h3>Caractéristiques du genre</h3>

        <ul>
          <li><strong>Routes de personnages</strong> — branches d&apos;histoire séparées pour chaque intérêt romantique</li>
          <li><strong>Système d&apos;affinité</strong> — les choix affectent les relations</li>
          <li><strong>Fins multiples</strong> — du bonheur à la tragédie</li>
          <li><strong>Moments clés</strong> — aveux, premier baiser, conflits</li>
        </ul>

        <h3>Archétypes de personnages</h3>

        <table>
          <thead>
            <tr>
              <th>Archétype</th>
              <th>Caractéristiques</th>
              <th>Exemple</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Tsundere</td>
              <td>Froid dehors, tendre dedans</td>
              <td>Hostilité → affection</td>
            </tr>
            <tr>
              <td>Kuudere</td>
              <td>Calme, sans émotion</td>
              <td>Ouverture émotionnelle progressive</td>
            </tr>
            <tr>
              <td>Yandere</td>
              <td>Amour obsessif</td>
              <td>Adorable → dangereux</td>
            </tr>
            <tr>
              <td>Dandere</td>
              <td>Timide, silencieux</td>
              <td>S&apos;ouvre progressivement</td>
            </tr>
            <tr>
              <td>Genki</td>
              <td>Énergique, joyeux</td>
              <td>Rayonne de positivité</td>
            </tr>
          </tbody>
        </table>

        <h3>Romance sur {BRAND}</h3>

        <p>Sur la plateforme <a href={localePath('/')}>{BRAND}</a> vous pouvez créer votre propre histoire romantique :</p>

        <ul>
          <li>Créez un personnage avec votre personnalité</li>
          <li>Développez les relations avec vos propres mots</li>
          <li>Chaque conversation est unique grâce à l&apos;IA</li>
          <li>Flirtez, avouez, disputez-vous — comme dans la vraie vie</li>
        </ul>

        <h2>Mystère et Mysticisme</h2>

        <h3>Qu&apos;est-ce que c&apos;est ?</h3>

        <p>Les romans visuels de mystère (Mystery VN) — des histoires où le joueur enquête sur des crimes, résout des énigmes ou découvre des secrets.</p>

        <h3>Sous-genres</h3>

        <p><strong>Détective classique</strong></p>
        <ul>
          <li>Enquête de meurtre</li>
          <li>Collecte de preuves et interrogatoires</li>
          <li>Déductions logiques</li>
          <li>Exemples : Ace Attorney, Danganronpa</li>
        </ul>

        <p><strong>Thriller mystique</strong></p>
        <ul>
          <li>Éléments surnaturels</li>
          <li>Enquêtes paranormales</li>
          <li>Exemples : Umineko no Naku Koro ni, Higurashi</li>
        </ul>

        <p><strong>Thriller psychologique</strong></p>
        <ul>
          <li>Jeux d&apos;esprit</li>
          <li>Narrateur peu fiable</li>
          <li>Exemples : Ever17, Remember11</li>
        </ul>

        <p><strong>Noir</strong></p>
        <ul>
          <li>Atmosphère sombre</li>
          <li>Protagoniste cynique</li>
          <li>Monde criminel</li>
        </ul>

        <h3>Caractéristiques du genre</h3>

        <ul>
          <li><strong>Système de preuves</strong> — le joueur collecte des preuves</li>
          <li><strong>Interrogatoires</strong> — contradictions dans les témoignages</li>
          <li><strong>Chaînes logiques</strong> — connexion des faits</li>
          <li><strong>Twists</strong> — rebondissements inattendus</li>
          <li><strong>Plusieurs suspects</strong> — qui est le vrai criminel ?</li>
        </ul>

        <h3>Éléments d&apos;un bon mystère</h3>

        <ol>
          <li><strong>Jeu équitable</strong> — tous les indices sont montré au joueur avant la solution</li>
          <li><strong>Motivation du méchant</strong> — raisons compréhensibles du crime</li>
          <li><strong>Fausses pistes</strong> — manœuvres distractives</li>
          <li><strong>Résolution satisfaisante</strong> — réponse logique et inattendue</li>
        </ol>

        <h3>Mystères sur {BRAND}</h3>

        <p><a href={localePath('/')}>{BRAND}</a> vous permet d&apos;enquêter sur des cas uniques :</p>

        <ul>
          <li>L&apos;IA crée des énigmes, s&apos;adaptant à vos actions</li>
          <li>Interrogez les suspects avec vos propres questions</li>
          <li>Chaque enquête est unique</li>
          <li>Devenir un détective dans un monde sans réponses prédéfinies</li>
          <li>Essayez le roman <a href={localePath('/production/labyrinthe-de-la-memoire')}>Labyrinthe de la Mémoire</a></li>
        </ul>

        <h2>Horreur</h2>

        <h3>Qu&apos;est-ce que c&apos;est ?</h3>

        <p>Les romans d&apos;horreur (Horror VN) — des histoires créées pour effrayer, causer de l&apos;anxiété et vous tenir en haleine.</p>

        <h3>Sous-genres</h3>

        <p><strong>Horreur psychologique</strong></p>
        <ul>
          <li>La peur par l&apos;atmosphère et l&apos;inconnu</li>
          <li>Jeux avec la perception de la réalité</li>
          <li>Exemples : Doki Doki Literature Club, Saya no Uta</li>
        </ul>

        <p><strong>Horreur surnaturelle</strong></p>
        <ul>
          <li>Fantômes, démons, malédictions</li>
          <li>La mythologie japonaise figure souvent</li>
          <li>Exemples : Corpse Party, Higurashi</li>
        </ul>

        <p><strong>Survival Horror</strong></p>
        <ul>
          <li>Survie en situations dangereuses</li>
          <li>Ressources limitées</li>
          <li>Monstres et poursuivants</li>
        </ul>

        <p><strong>Gothique</strong></p>
        <ul>
          <li>Châteaux et manoirs sombres</li>
          <li>Secrets de famille</li>
          <li>Atmosphère de décadence</li>
        </ul>

        <h3>Caractéristiques du genre</h3>

        <ul>
          <li><strong>Atmosphère</strong> — la musique, les visuels, le texte créent de l&apos;anxiété</li>
          <li><strong>Jump scares</strong> — moments effrayants soudains</li>
          <li><strong>Montée lente</strong> — la tension augmente progressivement</li>
          <li><strong>Fins mauvaises</strong> — la mort du héros est un résultat courant</li>
          <li><strong>Mystères</strong> — que se passe-t-il vraiment ?</li>
        </ul>

        <h3>Techniques de création de peur</h3>

        <table>
          <thead>
            <tr>
              <th>Technique</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>L&apos;inconnu</td>
              <td>La menace n&apos;est pas montrée directement</td>
            </tr>
            <tr>
              <td>Isolement</td>
              <td>Le héros est seul, pas d&apos;aide</td>
            </tr>
            <tr>
              <td>Impuissance</td>
              <td>Peut seulement courir, ne peut pas combattre</td>
            </tr>
            <tr>
              <td>Uncanny Valley</td>
              <td>Quelque chose de familier mais &quot;faux&quot;</td>
            </tr>
            <tr>
              <td>Silence</td>
              <td>Pause avant l&apos;horreur</td>
            </tr>
            <tr>
              <td>Attentes subverties</td>
              <td>Sûr devient dangereux</td>
            </tr>
          </tbody>
        </table>

        <h3>Horreur sur {BRAND}</h3>

        <p>Sur <a href={localePath('/')}>{BRAND}</a> les histoires d&apos;horreur sont particulièrement efficaces :</p>

        <ul>
          <li>L&apos;IA est imprévisible — vous ne savez pas ce qui vient ensuite</li>
          <li>Les personnages réagissent à votre peur</li>
          <li>Chaque passage apporte de nouvelles horreurs</li>
          <li>Vrai sentiment d&apos;inconnu</li>
          <li>Essayez le roman <a href={localePath('/production/derniere-nuit')}>Dernière Nuit</a></li>
        </ul>

        <h2>Fantasy</h2>

        <h3>Qu&apos;est-ce que c&apos;est ?</h3>

        <p>Les romans de fantasy transportent le joueur dans des mondes de magie, d&apos;épées et d&apos;aventures épiques.</p>

        <h3>Sous-genres</h3>

        <ul>
          <li><strong>Fantasy classique</strong> — elfes, dragons, quêtes</li>
          <li><strong>Fantasy sombre</strong> — monde sinistre, ambiguïté morale</li>
          <li><strong>Fantasy urbaine</strong> — la magie dans le monde moderne</li>
          <li><strong>Fantasy orientale</strong> — mythologie asiatique</li>
          <li><strong>LitRPG</strong> — mécaniques de jeu dans l&apos;intrigue</li>
        </ul>

        <h3>Caractéristiques du genre</h3>

        <ul>
          <li><strong>Système magique</strong> — comment fonctionne la magie</li>
          <li><strong>Worldbuilding</strong> — un monde bien pensé</li>
          <li><strong>Ampleur épique</strong> — le destin du monde en jeu</li>
          <li><strong>Aventures</strong> — voyages et batailles</li>
        </ul>

        <h2>Slice of Life</h2>

        <h3>Qu&apos;est-ce que c&apos;est ?</h3>

        <p>Slice of Life — des histoires sur la vie ordinaire sans conflits mondiaux.</p>

        <h3>Caractéristiques</h3>

        <ul>
          <li>École, travail, amitié</li>
          <li>Pas d&apos;antagoniste ou de catastrophe</li>
          <li>Accent sur les relations entre les gens</li>
          <li>Humour et moments émouvants</li>
          <li>Exemples : Été infini, Clannad (partiellement)</li>
        </ul>

        <h2>Science-fiction</h2>

        <h3>Qu&apos;est-ce que c&apos;est ?</h3>

        <p>Les romans de science-fiction explorent l&apos;impact de la technologie sur la société et l&apos;humanité.</p>

        <h3>Sous-genres</h3>

        <ul>
          <li><strong>Cyberpunk</strong> — technologie avancée, vie basse</li>
          <li><strong>Opéra spatial</strong> — aventures dans l&apos;espace</li>
          <li><strong>Après l&apos;apocalypse</strong> — monde après catastrophe</li>
          <li><strong>Boucles temporelles</strong> — voyages dans le temps</li>
        </ul>

        <h3>Exemples</h3>

        <ul>
          <li>Steins;Gate — voyages dans le temps</li>
          <li>AI: The Somnium Files — technologie de rêve</li>
          <li>Planetarian — robot et la fin du monde</li>
        </ul>

        <h2>Genres mélangés</h2>

        <p>Les meilleurs romans combinent souvent les genres :</p>

        <table>
          <thead>
            <tr>
              <th>Roman</th>
              <th>Genres</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Doki Doki Literature Club</td>
              <td>Romance + Horreur psychologique</td>
            </tr>
            <tr>
              <td>Danganronpa</td>
              <td>Mystère + Horreur + Thriller</td>
            </tr>
            <tr>
              <td>Steins;Gate</td>
              <td>Sci-Fi + Thriller + Romance</td>
            </tr>
            <tr>
              <td>Fate/stay night</td>
              <td>Fantasy + Action + Romance</td>
            </tr>
            <tr>
              <td>Umineko</td>
              <td>Mystère + Mysticisme + Fantasy</td>
            </tr>
          </tbody>
        </table>

        <h2>Comment choisir un genre pour toi</h2>

        <h3>Quiz : Quel genre te convient ?</h3>

        <p><strong>Qu&apos;est-ce qui compte le plus pour toi dans une histoire ?</strong></p>
        <ul>
          <li>Relations entre les personnages → <strong>Romance</strong></li>
          <li>Énigmes et logique → <strong>Mystère</strong></li>
          <li>Émotions et atmosphère → <strong>Horreur</strong> ou <strong>Drame</strong></li>
          <li>Aventures et action → <strong>Fantasy</strong></li>
          <li>Idées et concepts → <strong>Sci-Fi</strong></li>
        </ul>

        <h3>Essayez tout sur {BRAND}</h3>

        <p>La plateforme <a href={localePath('/')}>{BRAND}</a> offre des romans de tous les genres :</p>

        <ul>
          <li><strong>Romance</strong> — créez votre histoire d&apos;amour idéale</li>
          <li><strong>Mystère</strong> — enquêtez sur des cas uniques</li>
          <li><strong>Horreur</strong> — ressentez la véritable peur de l&apos;inconnu</li>
          <li><strong>Fantasy</strong> — devenir un héros d&apos;un monde magique</li>
        </ul>

        <p>Et le plus important — chaque histoire est unique grâce à l&apos;IA.</p>

        <h2>Conclusion</h2>

        <p>Les romans visuels couvrent tous les genres possibles — de la romance légère à l&apos;horreur sombre. Quel que soit le type d&apos;histoires que vous aimez, vous trouverez le roman parfait.</p>

        <p>Et si vous voulez essayer tous les genres avec une liberté maximale — <a href={localePath('/')}>{BRAND}</a> vous attend. Créez un personnage, choisissez un monde et vivez une histoire qui sera uniquement la vôtre.</p>

        <p><strong>Quel genre choisiras-tu ?</strong></p>
      </>
    ),
  },

  spanish: {
    title: '🎭 Géneros de novelas visuales: romance, misterio, horror',
    description: 'Las novelas visuales son un género multifacético con historias para todos los gustos. Exploremos los géneros principales.',
seoTitle: `Géneros de novelas visuales: romance, misterio, horror — ${BRAND}`,
    seoDescription: 'Las novelas visuales son un género multifacético con historias para todos los gustos. Del romance conmovedor al horror escalofriante — exploración de los géneros principales con ejemplos.',
    content: (
      <>
        <p>Las novelas visuales son un género multifacético que contiene historias para todos los gustos. Desde dramas románticos conmovedores hasta horrores escalofriantes — todos encontrarán algo que les guste. En este artículo, exploraremos los géneros principales de novelas visuales con ejemplos y características.</p>

        <h2>Romance</h2>

        <h3>¿Qué es?</h3>

        <p>Las novelas visuales románticas (Romance VN) — el género más popular, donde la historia se centra en el desarrollo de relaciones románticas.</p>

        <h3>Subgéneros de romance</h3>

        <p><strong>Otome (Otome Game)</strong></p>
        <ul>
          <li>Para audiencia femenina</li>
          <li>La heroína principal elige entre varios personajes masculinos</li>
          <li>Ejemplos: Mystic Messenger, Amnesia, Hakuoki</li>
        </ul>

        <p><strong>Galge (Galge/Bishoujo)</strong></p>
        <ul>
          <li>Para audiencia masculina</li>
          <li>El héroe principal desarrolla relaciones con personajes femeninos</li>
          <li>Ejemplos: Clannad, Katawa Shoujo, Doki Doki Literature Club</li>
        </ul>

        <p><strong>BL (Boys&apos; Love/Yaoi)</strong></p>
        <ul>
          <li>Romance entre personajes masculinos</li>
          <li>Ejemplos: Dramatical Murder, No, Thank You!!!</li>
        </ul>

        <p><strong>GL (Girls&apos; Love/Yuri)</strong></p>
        <ul>
          <li>Romance entre personajes femeninos</li>
          <li>Ejemplos: Kindred Spirits on the Roof, Highway Blossoms</li>
        </ul>

        <h3>Características del género</h3>

        <ul>
          <li><strong>Rutas de personajes</strong> — ramas de trama separadas para cada interés romántico</li>
          <li><strong>Sistema de afinidad</strong> — las elecciones afectan las relaciones</li>
          <li><strong>Múltiples finales</strong> — de felices a trágicos</li>
          <li><strong>Momentos clave</strong> — confesiones, primer beso, conflictos</li>
        </ul>

        <h3>Arquetipos de personajes</h3>

        <table>
          <thead>
            <tr>
              <th>Arquetipo</th>
              <th>Características</th>
              <th>Ejemplo</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Tsundere</td>
              <td>Frío afuera, tierno adentro</td>
              <td>Hostilidad → afecto</td>
            </tr>
            <tr>
              <td>Kuudere</td>
              <td>Tranquilo, sin emociones</td>
              <td>Apertura emocional gradual</td>
            </tr>
            <tr>
              <td>Yandere</td>
              <td>Amor obsesivo</td>
              <td>Encantador → peligroso</td>
            </tr>
            <tr>
              <td>Dandere</td>
              <td>Tímido, silencioso</td>
              <td>Se abre gradualmente</td>
            </tr>
            <tr>
              <td>Genki</td>
              <td>Energético, alegre</td>
              <td>Irradia positividad</td>
            </tr>
          </tbody>
        </table>

        <h3>Romance en {BRAND}</h3>

        <p>En la plataforma <a href={localePath('/')}>{BRAND}</a> puedes crear tu propia historia romántica:</p>

        <ul>
          <li>Crea un personaje con tu personalidad</li>
          <li>Desarrolla relaciones con tus propias palabras</li>
          <li>Cada conversación es única gracias a la IA</li>
          <li>Coquetea, confiesa, discute — como en la vida real</li>
        </ul>

        <h2>Misterio y Misticismo</h2>

        <h3>¿Qué es?</h3>

        <p>Las novelas visuales de misterio (Mystery VN) — historias donde el jugador investiga crímenes, resuelve acertijos o descubre secretos.</p>

        <h3>Subgéneros</h3>

        <p><strong>Detective clásico</strong></p>
        <ul>
          <li>Investigación de asesinato</li>
          <li>Recopilación de evidencia e interrogatorios</li>
          <li>Deducciones lógicas</li>
          <li>Ejemplos: Ace Attorney, Danganronpa</li>
        </ul>

        <p><strong>Thriller místico</strong></p>
        <ul>
          <li>Elementos sobrenaturales</li>
          <li>Investigaciones paranormales</li>
          <li>Ejemplos: Umineko no Naku Koro ni, Higurashi</li>
        </ul>

        <p><strong>Thriller psicológico</strong></p>
        <ul>
          <li>Juegos mentales</li>
          <li>Narrador no confiable</li>
          <li>Ejemplos: Ever17, Remember11</li>
        </ul>

        <p><strong>Noir</strong></p>
        <ul>
          <li>Atmósfera oscura</li>
          <li>Protagonista cínico</li>
          <li>Mundo criminal</li>
        </ul>

        <h3>Características del género</h3>

        <ul>
          <li><strong>Sistema de evidencia</strong> — el jugador recopila pruebas</li>
          <li><strong>Interrogatorios</strong> — contradicciones en el testimonio</li>
          <li><strong>Cadenas lógicas</strong> — conexión de hechos</li>
          <li><strong>Giros inesperados</strong> — vueltas sorprendentes</li>
          <li><strong>Múltiples sospechosos</strong> — ¿Quién es el verdadero criminal?</li>
        </ul>

        <h3>Elementos de un buen misterio</h3>

        <ol>
          <li><strong>Juego limpio</strong> — todas las pistas se muestran al jugador antes de la solución</li>
          <li><strong>Motivación del villano</strong> — razones comprensibles para el crimen</li>
          <li><strong>Pistas falsas</strong> — maniobras distractivas</li>
          <li><strong>Resolución satisfactoria</strong> — respuesta lógica e inesperada</li>
        </ol>

        <h3>Misterios en {BRAND}</h3>

        <p><a href={localePath('/')}>{BRAND}</a> te permite investigar casos únicos:</p>

        <ul>
          <li>La IA crea acertijos, adaptándose a tus acciones</li>
          <li>Interroga a sospechosos con tus propias preguntas</li>
          <li>Cada investigación es única</li>
          <li>Conviértete en detective en un mundo sin respuestas predeterminadas</li>
          <li>Prueba la novela <a href={localePath('/production/laberinto-de-la-memoria')}>Laberinto de la Memoria</a></li>
        </ul>

        <h2>Horror</h2>

        <h3>¿Qué es?</h3>

        <p>Las novelas de horror (Horror VN) — historias creadas para asustar, causar ansiedad y mantenerte en tensión.</p>

        <h3>Subgéneros</h3>

        <p><strong>Horror psicológico</strong></p>
        <ul>
          <li>Miedo a través de la atmósfera y lo desconocido</li>
          <li>Juegos con la percepción de la realidad</li>
          <li>Ejemplos: Doki Doki Literature Club, Saya no Uta</li>
        </ul>

        <p><strong>Horror sobrenatural</strong></p>
        <ul>
          <li>Fantasmas, demonios, maldiciones</li>
          <li>La mitología japonesa a menudo aparece</li>
          <li>Ejemplos: Corpse Party, Higurashi</li>
        </ul>

        <p><strong>Survival Horror</strong></p>
        <ul>
          <li>Supervivencia en situaciones peligrosas</li>
          <li>Recursos limitados</li>
          <li>Monstruos y perseguidores</li>
        </ul>

        <p><strong>Gótico</strong></p>
        <ul>
          <li>Castillos y mansiones oscuras</li>
          <li>Secretos familiares</li>
          <li>Atmósfera de decadencia</li>
        </ul>

        <h3>Características del género</h3>

        <ul>
          <li><strong>Atmósfera</strong> — la música, lo visual, el texto crean ansiedad</li>
          <li><strong>Jump scares</strong> — momentos asustadizos repentinos</li>
          <li><strong>Construcción lenta</strong> — la tensión crece gradualmente</li>
          <li><strong>Finales malos</strong> — la muerte del héroe es un resultado común</li>
          <li><strong>Misterios</strong> — ¿Qué está pasando realmente?</li>
        </ul>

        <h3>Técnicas para crear miedo</h3>

        <table>
          <thead>
            <tr>
              <th>Técnica</th>
              <th>Descripción</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Lo desconocido</td>
              <td>La amenaza no se muestra directamente</td>
            </tr>
            <tr>
              <td>Aislamiento</td>
              <td>El héroe está solo, sin ayuda</td>
            </tr>
            <tr>
              <td>Impotencia</td>
              <td>Solo puede correr, no puede pelear</td>
            </tr>
            <tr>
              <td>Uncanny Valley</td>
              <td>Algo familiar pero &quot;incorrecto&quot;</td>
            </tr>
            <tr>
              <td>Silencio</td>
              <td>Pausa antes del horror</td>
            </tr>
            <tr>
              <td>Expectativas subvertidas</td>
              <td>Lo seguro se vuelve peligroso</td>
            </tr>
          </tbody>
        </table>

        <h3>Horror en {BRAND}</h3>

        <p>En <a href={localePath('/')}>{BRAND}</a> las historias de horror son especialmente efectivas:</p>

        <ul>
          <li>La IA es impredecible — no sabes qué viene después</li>
          <li>Los personajes reaccionan a tu miedo</li>
          <li>Cada pasada trae nuevos horrores</li>
          <li>Verdadero sentido de lo desconocido</li>
          <li>Prueba la novela <a href={localePath('/production/ultima-noche')}>Última Noche</a></li>
        </ul>

        <h2>Fantasy</h2>

        <h3>¿Qué es?</h3>

        <p>Las novelas de fantasía transportan al jugador a mundos de magia, espadas y aventuras épicas.</p>

        <h3>Subgéneros</h3>

        <ul>
          <li><strong>Fantasy clásico</strong> — elfos, dragones, misiones</li>
          <li><strong>Fantasy oscuro</strong> — mundo sombrío, ambigüedad moral</li>
          <li><strong>Fantasy urbano</strong> — magia en el mundo moderno</li>
          <li><strong>Fantasy oriental</strong> — mitología asiática</li>
          <li><strong>LitRPG</strong> — mecánicas de juego en la trama</li>
        </ul>

        <h3>Características del género</h3>

        <ul>
          <li><strong>Sistema mágico</strong> — cómo funciona la magia</li>
          <li><strong>Worldbuilding</strong> — un mundo bien pensado</li>
          <li><strong>Escala épica</strong> — el destino del mundo en juego</li>
          <li><strong>Aventuras</strong> — viajes y batallas</li>
        </ul>

        <h2>Slice of Life</h2>

        <h3>¿Qué es?</h3>

        <p>Slice of Life — historias sobre la vida ordinaria sin conflictos globales.</p>

        <h3>Características</h3>

        <ul>
          <li>Escuela, trabajo, amistad</li>
          <li>Sin antagonista o catástrofe</li>
          <li>Enfoque en relaciones entre personas</li>
          <li>Humor y momentos conmovedores</li>
          <li>Ejemplos: Verano Infinito, Clannad (parcialmente)</li>
        </ul>

        <h2>Ciencia Ficción</h2>

        <h3>¿Qué es?</h3>

        <p>Las novelas de ciencia ficción exploran el impacto de la tecnología en la sociedad y la humanidad.</p>

        <h3>Subgéneros</h3>

        <ul>
          <li><strong>Cyberpunk</strong> — tecnología alta, vida baja</li>
          <li><strong>Ópera espacial</strong> — aventuras en el espacio</li>
          <li><strong>Postapocalipsis</strong> — mundo después de la catástrofe</li>
          <li><strong>Bucles temporales</strong> — viajes en el tiempo</li>
        </ul>

        <h3>Ejemplos</h3>

        <ul>
          <li>Steins;Gate — viajes en el tiempo</li>
          <li>AI: The Somnium Files — tecnología de sueños</li>
          <li>Planetarian — robot y el fin del mundo</li>
        </ul>

        <h2>Géneros mezclados</h2>

        <p>Los mejores novelas a menudo combinan géneros:</p>

        <table>
          <thead>
            <tr>
              <th>Novela</th>
              <th>Géneros</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Doki Doki Literature Club</td>
              <td>Romance + Horror psicológico</td>
            </tr>
            <tr>
              <td>Danganronpa</td>
              <td>Misterio + Horror + Thriller</td>
            </tr>
            <tr>
              <td>Steins;Gate</td>
              <td>Sci-Fi + Thriller + Romance</td>
            </tr>
            <tr>
              <td>Fate/stay night</td>
              <td>Fantasy + Acción + Romance</td>
            </tr>
            <tr>
              <td>Umineko</td>
              <td>Misterio + Misticismo + Fantasy</td>
            </tr>
          </tbody>
        </table>

        <h2>Cómo elegir un género para ti</h2>

        <h3>Quiz: ¿Qué género te conviene?</h3>

        <p><strong>¿Qué es lo más importante para ti en una historia?</strong></p>
        <ul>
          <li>Relaciones entre personajes → <strong>Romance</strong></li>
          <li>Acertijos y lógica → <strong>Misterio</strong></li>
          <li>Emociones y atmósfera → <strong>Horror</strong> o <strong>Drama</strong></li>
          <li>Aventuras y acción → <strong>Fantasy</strong></li>
          <li>Ideas y conceptos → <strong>Sci-Fi</strong></li>
        </ul>

        <h3>Prueba todo en {BRAND}</h3>

        <p>La plataforma <a href={localePath('/')}>{BRAND}</a> ofrece novelas de todos los géneros:</p>

        <ul>
          <li><strong>Romance</strong> — crea tu historia de amor ideal</li>
          <li><strong>Misterio</strong> — investiga casos únicos</li>
          <li><strong>Horror</strong> — experimenta el verdadero miedo a lo desconocido</li>
          <li><strong>Fantasy</strong> — conviértete en héroe de un mundo mágico</li>
        </ul>

        <p>Y lo más importante — cada historia es única gracias a la IA.</p>

        <h2>Conclusión</h2>

        <p>Las novelas visuales cubren todos los géneros posibles — desde romance ligero hasta horror oscuro. Sea cual sea el tipo de historias que te gusten, encontrarás la novela perfecta.</p>

        <p>Y si quieres probar todos los géneros con máxima libertad — <a href={localePath('/')}>{BRAND}</a> te espera. Crea un personaje, elige un mundo y vive una historia que será solo tuya.</p>

        <p><strong>¿Qué género elegirás?</strong></p>
      </>
    ),
  },

  italian: {
    title: '🎭 Generi di romanzi visuali: romance, mistero, horror',
    description: 'I romanzi visuali sono un genere sfaccettato con storie per tutti i gusti. Esploriamo i generi principali.',
seoTitle: `Generi di romanzi visuali: romance, mistero, horror — ${BRAND}`,
    seoDescription: "I romanzi visuali sono un genere sfaccettato con storie per tutti i gusti. Dal romance toccante all'horror inquietante — esplorazione dei generi principali con esempi.",
    content: (
      <>
        <p>I romanzi visuali sono un genere sfaccettato che contiene storie per tutti i gusti. Dai drammi romantici toccanti agli orrori inquietanti — tutti troveranno qualcosa che piace. In questo articolo, esploreremo i principali generi di romanzi visuali con esempi e caratteristiche.</p>

        <h2>Romance</h2>

        <h3>Che cos&apos;è?</h3>

        <p>I romanzi visuali romantici (Romance VN) — il genere più popolare, dove la storia si concentra sullo sviluppo delle relazioni romantiche.</p>

        <h3>Sottogeneri di romance</h3>

        <p><strong>Otome (Otome Game)</strong></p>
        <ul>
          <li>Per il pubblico femminile</li>
          <li>L&apos;eroina principale sceglie tra diversi personaggi maschili</li>
          <li>Esempi: Mystic Messenger, Amnesia, Hakuoki</li>
        </ul>

        <p><strong>Galge (Galge/Bishoujo)</strong></p>
        <ul>
          <li>Per il pubblico maschile</li>
          <li>L&apos;eroe principale sviluppa relazioni con personaggi femminili</li>
          <li>Esempi: Clannad, Katawa Shoujo, Doki Doki Literature Club</li>
        </ul>

        <p><strong>BL (Boys&apos; Love/Yaoi)</strong></p>
        <ul>
          <li>Romance tra personaggi maschili</li>
          <li>Esempi: Dramatical Murder, No, Thank You!!!</li>
        </ul>

        <p><strong>GL (Girls&apos; Love/Yuri)</strong></p>
        <ul>
          <li>Romance tra personaggi femminili</li>
          <li>Esempi: Kindred Spirits on the Roof, Highway Blossoms</li>
        </ul>

        <h3>Caratteristiche del genere</h3>

        <ul>
          <li><strong>Percorsi dei personaggi</strong> — rami di trama separati per ogni interesse romantico</li>
          <li><strong>Sistema di affinità</strong> — le scelte influenzano le relazioni</li>
          <li><strong>Molteplici finali</strong> — da felici a tragici</li>
          <li><strong>Momenti chiave</strong> — confessioni, primo bacio, conflitti</li>
        </ul>

        <h3>Archetipi di personaggi</h3>

        <table>
          <thead>
            <tr>
              <th>Archetipo</th>
              <th>Caratteristiche</th>
              <th>Esempio</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Tsundere</td>
              <td>Freddo fuori, tenero dentro</td>
              <td>Ostilità → affetto</td>
            </tr>
            <tr>
              <td>Kuudere</td>
              <td>Calmo, senza emozioni</td>
              <td>Apertura emotiva graduale</td>
            </tr>
            <tr>
              <td>Yandere</td>
              <td>Amore ossessivo</td>
              <td>Dolce → pericoloso</td>
            </tr>
            <tr>
              <td>Dandere</td>
              <td>Timido, tranquillo</td>
              <td>Si apre gradualmente</td>
            </tr>
            <tr>
              <td>Genki</td>
              <td>Energico, allegro</td>
              <td>Irradia positività</td>
            </tr>
          </tbody>
        </table>

        <h3>Romance su {BRAND}</h3>

        <p>Sulla piattaforma <a href={localePath('/')}>{BRAND}</a> puoi creare la tua storia romantica:</p>

        <ul>
          <li>Crea un personaggio con la tua personalità</li>
          <li>Sviluppa le relazioni con le tue stesse parole</li>
          <li>Ogni conversazione è unica grazie all&apos;IA</li>
          <li>Flirta, confessa, discuti — come nella vita reale</li>
        </ul>

        <h2>Mistero e Misticismo</h2>

        <h3>Che cos&apos;è?</h3>

        <p>I romanzi visuali di mistero (Mystery VN) — storie in cui il giocatore indaga su crimini, risolve enigmi o scopre segreti.</p>

        <h3>Sottogeneri</h3>

        <p><strong>Detective classico</strong></p>
        <ul>
          <li>Indagine su un omicidio</li>
          <li>Raccolta di prove e interrogatori</li>
          <li>Deduzioni logiche</li>
          <li>Esempi: Ace Attorney, Danganronpa</li>
        </ul>

        <p><strong>Thriller mistico</strong></p>
        <ul>
          <li>Elementi sovrumani</li>
          <li>Indagini paranormali</li>
          <li>Esempi: Umineko no Naku Koro ni, Higurashi</li>
        </ul>

        <p><strong>Thriller psicologico</strong></p>
        <ul>
          <li>Giochi mentali</li>
          <li>Narratore inaffidabile</li>
          <li>Esempi: Ever17, Remember11</li>
        </ul>

        <p><strong>Noir</strong></p>
        <ul>
          <li>Atmosfera scura</li>
          <li>Protagonista cinico</li>
          <li>Mondo criminale</li>
        </ul>

        <h3>Caratteristiche del genere</h3>

        <ul>
          <li><strong>Sistema di prove</strong> — il giocatore raccoglie le prove</li>
          <li><strong>Interrogatori</strong> — contraddizioni nella testimonianza</li>
          <li><strong>Catene logiche</strong> — connessione dei fatti</li>
          <li><strong>Colpi di scena</strong> — giri inaspettati</li>
          <li><strong>Molteplici sospettati</strong> — chi è il vero criminale?</li>
        </ul>

        <h3>Elementi di un buon mistero</h3>

        <ol>
          <li><strong>Gioco leale</strong> — tutti gli indizi vengono mostrati al giocatore prima della soluzione</li>
          <li><strong>Motivazione del cattivo</strong> — ragioni comprensibili per il crimine</li>
          <li><strong>Piste false</strong> — manovre distraenti</li>
          <li><strong>Risoluzione soddisfacente</strong> — risposta logica e inaspettata</li>
        </ol>

        <h3>Misteri su {BRAND}</h3>

        <p><a href={localePath('/')}>{BRAND}</a> ti permette di indagare su casi unici:</p>

        <ul>
          <li>L&apos;IA crea enigmi, adattandosi alle tue azioni</li>
          <li>Interroga i sospettati con le tue stesse domande</li>
          <li>Ogni indagine è unica</li>
          <li>Diventa un detective in un mondo senza risposte prestabilite</li>
          <li>Prova il romanzo <a href={localePath('/production/labirinto-della-memoria')}>Labirinto della Memoria</a></li>
        </ul>

        <h2>Horror</h2>

        <h3>Che cos&apos;è?</h3>

        <p>I romanzi di horror (Horror VN) — storie create per spaventare, causare ansia e mantenerti in tensione.</p>

        <h3>Sottogeneri</h3>

        <p><strong>Horror psicologico</strong></p>
        <ul>
          <li>La paura attraverso l&apos;atmosfera e l&apos;ignoto</li>
          <li>Giochi con la percezione della realtà</li>
          <li>Esempi: Doki Doki Literature Club, Saya no Uta</li>
        </ul>

        <p><strong>Horror sovrumano</strong></p>
        <ul>
          <li>Fantasmi, demoni, maledizioni</li>
          <li>La mitologia giapponese è spesso presente</li>
          <li>Esempi: Corpse Party, Higurashi</li>
        </ul>

        <p><strong>Survival Horror</strong></p>
        <ul>
          <li>Sopravvivenza in situazioni pericolose</li>
          <li>Risorse limitate</li>
          <li>Mostri e inseguitori</li>
        </ul>

        <p><strong>Gotico</strong></p>
        <ul>
          <li>Castelli e dimore scuri</li>
          <li>Segreti di famiglia</li>
          <li>Atmosfera di decadenza</li>
        </ul>

        <h3>Caratteristiche del genere</h3>

        <ul>
          <li><strong>Atmosfera</strong> — musica, elementi visivi, testo creano ansia</li>
          <li><strong>Jump scares</strong> — momenti spaventosi improvvisi</li>
          <li><strong>Costruzione lenta</strong> — la tensione cresce gradualmente</li>
          <li><strong>Finali cattivi</strong> — la morte dell&apos;eroe è un risultato comune</li>
          <li><strong>Misteri</strong> — cosa sta realmente accadendo?</li>
        </ul>

        <h3>Tecniche per creare paura</h3>

        <table>
          <thead>
            <tr>
              <th>Tecnica</th>
              <th>Descrizione</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>L&apos;ignoto</td>
              <td>La minaccia non è mostrata direttamente</td>
            </tr>
            <tr>
              <td>Isolamento</td>
              <td>L&apos;eroe è solo, nessun aiuto</td>
            </tr>
            <tr>
              <td>Impotenza</td>
              <td>Può solo scappare, non può combattere</td>
            </tr>
            <tr>
              <td>Uncanny Valley</td>
              <td>Qualcosa di familiare ma &quot;sbagliato&quot;</td>
            </tr>
            <tr>
              <td>Silenzio</td>
              <td>Pausa prima dell&apos;orrore</td>
            </tr>
            <tr>
              <td>Aspettative sovvertite</td>
              <td>Sicuro diventa pericoloso</td>
            </tr>
          </tbody>
        </table>

        <h3>Horror su {BRAND}</h3>

        <p>Su <a href={localePath('/')}>{BRAND}</a> le storie di horror sono particolarmente efficaci:</p>

        <ul>
          <li>L&apos;IA è imprevedibile — non sai cosa viene dopo</li>
          <li>I personaggi reagiscono alla tua paura</li>
          <li>Ogni passaggio porta nuovi orrori</li>
          <li>Vero senso dell&apos;ignoto</li>
          <li>Prova il romanzo <a href={localePath('/production/ultima-notte')}>Ultima Notte</a></li>
        </ul>

        <h2>Fantasy</h2>

        <h3>Che cos&apos;è?</h3>

        <p>I romanzi di fantasy trasportano il giocatore in mondi di magia, spade e avventure epiche.</p>

        <h3>Sottogeneri</h3>

        <ul>
          <li><strong>Fantasy classico</strong> — elfi, draghi, missioni</li>
          <li><strong>Fantasy scuro</strong> — mondo tetro, ambiguità morale</li>
          <li><strong>Fantasy urbano</strong> — magia nel mondo moderno</li>
          <li><strong>Fantasy orientale</strong> — mitologia asiatica</li>
          <li><strong>LitRPG</strong> — meccaniche di gioco nella trama</li>
        </ul>

        <h3>Caratteristiche del genere</h3>

        <ul>
          <li><strong>Sistema magico</strong> — come funziona la magia</li>
          <li><strong>Costruzione del mondo</strong> — un mondo ben pensato</li>
          <li><strong>Scala epica</strong> — il destino del mondo in gioco</li>
          <li><strong>Avventure</strong> — viaggi e battaglie</li>
        </ul>

        <h2>Slice of Life</h2>

        <h3>Che cos&apos;è?</h3>

        <p>Slice of Life — storie sulla vita ordinaria senza conflitti globali.</p>

        <h3>Caratteristiche</h3>

        <ul>
          <li>Scuola, lavoro, amicizia</li>
          <li>Nessun antagonista o catastrofe</li>
          <li>Attenzione alle relazioni tra le persone</li>
          <li>Umorismo e momenti toccanti</li>
          <li>Esempi: Estate Infinita, Clannad (parzialmente)</li>
        </ul>

        <h2>Fantascienza</h2>

        <h3>Che cos&apos;è?</h3>

        <p>I romanzi di fantascienza esplorano l&apos;impatto della tecnologia sulla società e l&apos;umanità.</p>

        <h3>Sottogeneri</h3>

        <ul>
          <li><strong>Cyberpunk</strong> — tecnologia avanzata, vita bassa</li>
          <li><strong>Opera spaziale</strong> — avventure nello spazio</li>
          <li><strong>Post-apocalisse</strong> — mondo dopo la catastrofe</li>
          <li><strong>Cicli temporali</strong> — viaggi nel tempo</li>
        </ul>

        <h3>Esempi</h3>

        <ul>
          <li>Steins;Gate — viaggi nel tempo</li>
          <li>AI: The Somnium Files — tecnologia dei sogni</li>
          <li>Planetarian — robot e la fine del mondo</li>
        </ul>

        <h2>Generi misti</h2>

        <p>I migliori romanzi spesso combinano i generi:</p>

        <table>
          <thead>
            <tr>
              <th>Romanzo</th>
              <th>Generi</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Doki Doki Literature Club</td>
              <td>Romance + Horror psicologico</td>
            </tr>
            <tr>
              <td>Danganronpa</td>
              <td>Mistero + Horror + Thriller</td>
            </tr>
            <tr>
              <td>Steins;Gate</td>
              <td>Fantascienza + Thriller + Romance</td>
            </tr>
            <tr>
              <td>Fate/stay night</td>
              <td>Fantasy + Azione + Romance</td>
            </tr>
            <tr>
              <td>Umineko</td>
              <td>Mistero + Misticismo + Fantasy</td>
            </tr>
          </tbody>
        </table>

        <h2>Come scegliere un genere per te</h2>

        <h3>Quiz: Quale genere ti si addice?</h3>

        <p><strong>Cosa conta di più per te in una storia?</strong></p>
        <ul>
          <li>Relazioni tra i personaggi → <strong>Romance</strong></li>
          <li>Enigmi e logica → <strong>Mistero</strong></li>
          <li>Emozioni e atmosfera → <strong>Horror</strong> o <strong>Dramma</strong></li>
          <li>Avventure e azione → <strong>Fantasy</strong></li>
          <li>Idee e concetti → <strong>Fantascienza</strong></li>
        </ul>

        <h3>Prova tutto su {BRAND}</h3>

        <p>La piattaforma <a href={localePath('/')}>{BRAND}</a> offre romanzi di tutti i generi:</p>

        <ul>
          <li><strong>Romance</strong> — crea la tua storia d&apos;amore ideale</li>
          <li><strong>Mistero</strong> — indaga su casi unici</li>
          <li><strong>Horror</strong> — sperimenta la vera paura dell&apos;ignoto</li>
          <li><strong>Fantasy</strong> — diventa un eroe di un mondo magico</li>
        </ul>

        <p>E la cosa più importante — ogni storia è unica grazie all&apos;IA.</p>

        <h2>Conclusione</h2>

        <p>I romanzi visuali coprono tutti i generi possibili — dal romance leggero all&apos;horror scuro. Qualunque sia il tipo di storie che ami, troverai il romanzo perfetto.</p>

        <p>E se vuoi provare tutti i generi con la massima libertà — <a href={localePath('/')}>{BRAND}</a> ti aspetta. Crea un personaggio, scegli un mondo e vivi una storia che sarà solo tua.</p>

        <p><strong>Quale genere sceglierai?</strong></p>
      </>
    ),
  },

  polish: {
    title: '🎭 Gatunki powieści wizualnych: romans, mystyka, horror',
    description: 'Powieści wizualne to gatunek wielowymiarowy z historiami dla każdego smaku. Poznajmy główne gatunki.',
seoTitle: `Gatunki powieści wizualnych: romans, mystyka, horror — ${BRAND}`,
    seoDescription: 'Powieści wizualne to gatunek wielowymiarowy z historiami dla każdego smaku. Od wzruszającego romansu do mrożącego horroru — eksploracja głównych gatunków z przykładami.',
    content: (
      <>
        <p>Powieści wizualne to gatunek wielowymiarowy, który zawiera historie na każdy gust. Od poruszających dramatów romantycznych po mrożące horrory — każdy znajdzie coś dla siebie. W tym artykule poznamy główne gatunki powieści wizualnych z przykładami i charakterystyką.</p>

        <h2>Romans</h2>

        <h3>Czym jest?</h3>

        <p>Romantyczne powieści wizualne (Romance VN) — najpopularniejszy gatunek, w którym historia koncentruje się na rozwoju relacji romantycznych.</p>

        <h3>Podgatunki romansu</h3>

        <p><strong>Otome (Otome Game)</strong></p>
        <ul>
          <li>Dla publiczności żeńskiej</li>
          <li>Główna bohaterka wybiera spośród kilku postaci męskich</li>
          <li>Przykłady: Mystic Messenger, Amnesia, Hakuoki</li>
        </ul>

        <p><strong>Galge (Galge/Bishoujo)</strong></p>
        <ul>
          <li>Dla publiczności męskiej</li>
          <li>Główny bohater rozwija relacje z postaciami żeńskimi</li>
          <li>Przykłady: Clannad, Katawa Shoujo, Doki Doki Literature Club</li>
        </ul>

        <p><strong>BL (Boys&apos; Love/Yaoi)</strong></p>
        <ul>
          <li>Romans między postaciami męskimi</li>
          <li>Przykłady: Dramatical Murder, No, Thank You!!!</li>
        </ul>

        <p><strong>GL (Girls&apos; Love/Yuri)</strong></p>
        <ul>
          <li>Romans między postaciami żeńskimi</li>
          <li>Przykłady: Kindred Spirits on the Roof, Highway Blossoms</li>
        </ul>

        <h3>Cechy gatunku</h3>

        <ul>
          <li><strong>Trasy postaci</strong> — oddzielne gałęzie fabuły dla każdego zainteresowania romantycznego</li>
          <li><strong>System zażyłości</strong> — wybory wpływają na relacje</li>
          <li><strong>Wiele zakończeń</strong> — od szczęśliwych do tragicznych</li>
          <li><strong>Kluczowe momenty</strong> — wyznanie uczuć, pierwszy pocałunek, konflikty</li>
        </ul>

        <h3>Archetypy postaci</h3>

        <table>
          <thead>
            <tr>
              <th>Archetyp</th>
              <th>Charakterystyka</th>
              <th>Przykład</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Tsundere</td>
              <td>Zimny na zewnątrz, czuły wewnątrz</td>
              <td>Wrogość → czułość</td>
            </tr>
            <tr>
              <td>Kuudere</td>
              <td>Spokojny, bezuczuciowy</td>
              <td>Stopniowe otwarcie się emocjonalne</td>
            </tr>
            <tr>
              <td>Yandere</td>
              <td>Obsesyjna miłość</td>
              <td>Słodki → niebezpieczny</td>
            </tr>
            <tr>
              <td>Dandere</td>
              <td>Nieśmiały, cichy</td>
              <td>Stopniowo się otwiera</td>
            </tr>
            <tr>
              <td>Genki</td>
              <td>Energiczny, wesoły</td>
              <td>Emanuje pozytywność</td>
            </tr>
          </tbody>
        </table>

        <h3>Romans na {BRAND}</h3>

        <p>Na platformie <a href={localePath('/')}>{BRAND}</a> możesz stworzyć swoją własną historię romantyczną:</p>

        <ul>
          <li>Utwórz postać z Twoją osobowością</li>
          <li>Rozwijaj relacje Twoimi słowami</li>
          <li>Każda rozmowa jest unikalna dzięki AI</li>
          <li>Flirtuj, przyznawaj się, kłóć się — jak w prawdziwym życiu</li>
        </ul>

        <h2>Mystyka i Tajemnica</h2>

        <h3>Czym jest?</h3>

        <p>Powieści wizualne z tajemnicą (Mystery VN) — historie, w których gracz prowadzi śledztwo w sprawie zbrodni, rozwiązuje zagadki lub odkrywa sekrety.</p>

        <h3>Podgatunki</h3>

        <p><strong>Klasyczna detektywka</strong></p>
        <ul>
          <li>Śledztwo w sprawie morderstwa</li>
          <li>Zbieranie dowodów i przesłuchania</li>
          <li>Logiczne wnioskowanie</li>
          <li>Przykłady: Ace Attorney, Danganronpa</li>
        </ul>

        <p><strong>Thriller mistyczny</strong></p>
        <ul>
          <li>Elementy nadprzyrodzone</li>
          <li>Parapsychologiczne śledztwa</li>
          <li>Przykłady: Umineko no Naku Koro ni, Higurashi</li>
        </ul>

        <p><strong>Thriller psychologiczny</strong></p>
        <ul>
          <li>Gry umysłu</li>
          <li>Nierzetelny narrator</li>
          <li>Przykłady: Ever17, Remember11</li>
        </ul>

        <p><strong>Noir</strong></p>
        <ul>
          <li>Mroczna atmosfera</li>
          <li>Cyniczny protagonista</li>
          <li>Świat przestępczości</li>
        </ul>

        <h3>Cechy gatunku</h3>

        <ul>
          <li><strong>System dowodów</strong> — gracz zbiera dowody</li>
          <li><strong>Przesłuchania</strong> — sprzeczności w zeznaniach</li>
          <li><strong>Łańcuchy logiczne</strong> — połączenie faktów</li>
          <li><strong>Zwroty akcji</strong> — niespodziewane przewroty</li>
          <li><strong>Wielu podejrzanych</strong> — kto jest prawdziwym przestępcą?</li>
        </ul>

        <h3>Elementy dobrej tajemnicy</h3>

        <ol>
          <li><strong>Uczciwa gra</strong> — wszystkie wskazówki pokazywane graczu przed rozwiązaniem</li>
          <li><strong>Motywacja złoczyńcy</strong> — zrozumiałe przyczyny zbrodni</li>
          <li><strong>Fałszywe tropy</strong> — manewry odwracające uwagę</li>
          <li><strong>Satysfakcjonujące rozwiązanie</strong> — logiczna i nieoczekiwana odpowiedź</li>
        </ol>

        <h3>Tajemnice na {BRAND}</h3>

        <p><a href={localePath('/')}>{BRAND}</a> pozwala na zbadanie unikalnych spraw:</p>

        <ul>
          <li>AI tworzy zagadki, dostosowując się do Twoich działań</li>
          <li>Przesłuchuj podejrzanych własnymi pytaniami</li>
          <li>Każde śledztwo jest unikalne</li>
          <li>Zostań detektywem w świecie bez z góry gotowych odpowiedzi</li>
          <li>Spróbuj powieści <a href={localePath('/production/labirynt-pamieci')}>Labirynt Pamięci</a></li>
        </ul>

        <h2>Horror</h2>

        <h3>Czym jest?</h3>

        <p>Powieści horrorowe (Horror VN) — historie stworzone, aby przerażać, wywoływać niepokój i trzymać Cię w napięciu.</p>

        <h3>Podgatunki</h3>

        <p><strong>Horror psychologiczny</strong></p>
        <ul>
          <li>Strach poprzez atmosferę i nieznane</li>
          <li>Gry z postrzeganiem rzeczywistości</li>
          <li>Przykłady: Doki Doki Literature Club, Saya no Uta</li>
        </ul>

        <p><strong>Horror nadprzyrodzony</strong></p>
        <ul>
          <li>Duchy, demony, klątwy</li>
          <li>Japońska mitologia często się pojawia</li>
          <li>Przykłady: Corpse Party, Higurashi</li>
        </ul>

        <p><strong>Survival Horror</strong></p>
        <ul>
          <li>Przetrwanie w niebezpiecznych sytuacjach</li>
          <li>Ograniczone zasoby</li>
          <li>Potwory i prześladowcy</li>
        </ul>

        <p><strong>Gotyka</strong></p>
        <ul>
          <li>Mroczne zamki i dwory</li>
          <li>Rodzinne sekrety</li>
          <li>Atmosfera upadku</li>
        </ul>

        <h3>Cechy gatunku</h3>

        <ul>
          <li><strong>Atmosfera</strong> — muzyka, wizualizacja, tekst tworzą niepokój</li>
          <li><strong>Jump scares</strong> — nagłe przerażające momenty</li>
          <li><strong>Powolne narastanie</strong> — napięcie rośnie stopniowo</li>
          <li><strong>Złe zakończenia</strong> — śmierć bohatera to częsty rezultat</li>
          <li><strong>Tajemnice</strong> — co się naprawdę dzieje?</li>
        </ul>

        <h3>Techniki tworzenia strachu</h3>

        <table>
          <thead>
            <tr>
              <th>Technika</th>
              <th>Opis</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Nieznane</td>
              <td>Zagrożenie nie jest pokazane bezpośrednio</td>
            </tr>
            <tr>
              <td>Izolacja</td>
              <td>Bohater jest sam, brak pomocy</td>
            </tr>
            <tr>
              <td>Bezsilność</td>
              <td>Może tylko uciekać, nie może walczyć</td>
            </tr>
            <tr>
              <td>Uncanny Valley</td>
              <td>Coś znajomego, ale &quot;błędne&quot;</td>
            </tr>
            <tr>
              <td>Cisza</td>
              <td>Pauza przed horrorem</td>
            </tr>
            <tr>
              <td>Obalenie oczekiwań</td>
              <td>Bezpieczne staje się niebezpieczne</td>
            </tr>
          </tbody>
        </table>

        <h3>Horror na {BRAND}</h3>

        <p>Na <a href={localePath('/')}>{BRAND}</a> historie horroru są szczególnie efektywne:</p>

        <ul>
          <li>AI jest nieprzewidywalna — nie wiesz, co będzie dalej</li>
          <li>Postacie reagują na Twój strach</li>
          <li>Każdą przygodę towarzyszą nowe horrory</li>
          <li>Prawdziwe poczucie nieznanych</li>
          <li>Spróbuj powieści <a href={localePath('/production/ostatnia-noc')}>Ostatnia Noc</a></li>
        </ul>

        <h2>Fantasy</h2>

        <h3>Czym jest?</h3>

        <p>Powieści fantasy przenoszą gracza do światów magii, mieczy i epicznych przygód.</p>

        <h3>Podgatunki</h3>

        <ul>
          <li><strong>Klasyczne fantasy</strong> — elfy, smoki, questy</li>
          <li><strong>Mroczne fantasy</strong> — ponury świat, moralna niejednoznaczność</li>
          <li><strong>Urban fantasy</strong> — magia we współczesnym świecie</li>
          <li><strong>Orientalne fantasy</strong> — azjatycka mitologia</li>
          <li><strong>LitRPG</strong> — mechaniki gry w fabule</li>
        </ul>

        <h3>Cechy gatunku</h3>

        <ul>
          <li><strong>System magiczny</strong> — jak działa magia</li>
          <li><strong>Budowanie świata</strong> — zamyślony świat</li>
          <li><strong>Skala epiczna</strong> — los świata jest zagrożony</li>
          <li><strong>Przygody</strong> — podróże i walki</li>
        </ul>

        <h2>Slice of Life</h2>

        <h3>Czym jest?</h3>

        <p>Slice of Life — historie o zwyczajnym życiu bez globalnych konfliktów.</p>

        <h3>Charakterystyka</h3>

        <ul>
          <li>Szkoła, praca, przyjaźń</li>
          <li>Brak antagonisty lub katastrofy</li>
          <li>Fokus na relacjach między ludźmi</li>
          <li>Humor i wzruszające momenty</li>
          <li>Przykłady: Nieskończone Lato, Clannad (częściowo)</li>
        </ul>

        <h2>Science Fiction</h2>

        <h3>Czym jest?</h3>

        <p>Powieści SF badają wpływ technologii na społeczeństwo i ludzkość.</p>

        <h3>Podgatunki</h3>

        <ul>
          <li><strong>Cyberpunk</strong> — zaawansowana technologia, niska jakość życia</li>
          <li><strong>Kosmoopera</strong> — przygody w kosmosie</li>
          <li><strong>Post-apokalipsa</strong> — świat po katastrofie</li>
          <li><strong>Pętle czasowe</strong> — podróże w czasie</li>
        </ul>

        <h3>Przykłady</h3>

        <ul>
          <li>Steins;Gate — podróże w czasie</li>
          <li>AI: The Somnium Files — technologia marzeń</li>
          <li>Planetarian — robot i koniec świata</li>
        </ul>

        <h2>Mieszane gatunki</h2>

        <p>Najlepsze powieści często łączą gatunki:</p>

        <table>
          <thead>
            <tr>
              <th>Powieść</th>
              <th>Gatunki</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Doki Doki Literature Club</td>
              <td>Romans + Horror psychologiczny</td>
            </tr>
            <tr>
              <td>Danganronpa</td>
              <td>Tajemnica + Horror + Thriller</td>
            </tr>
            <tr>
              <td>Steins;Gate</td>
              <td>SF + Thriller + Romans</td>
            </tr>
            <tr>
              <td>Fate/stay night</td>
              <td>Fantasy + Akcja + Romans</td>
            </tr>
            <tr>
              <td>Umineko</td>
              <td>Tajemnica + Mistyka + Fantasy</td>
            </tr>
          </tbody>
        </table>

        <h2>Jak wybrać gatunek dla siebie</h2>

        <h3>Quiz: Jaki gatunek do Ciebie pasuje?</h3>

        <p><strong>Co jest dla Ciebie najważniejsze w historii?</strong></p>
        <ul>
          <li>Relacje między postaciami → <strong>Romans</strong></li>
          <li>Zagadki i logika → <strong>Tajemnica</strong></li>
          <li>Emocje i atmosfera → <strong>Horror</strong> lub <strong>Dramat</strong></li>
          <li>Przygody i akcja → <strong>Fantasy</strong></li>
          <li>Idee i koncepcje → <strong>SF</strong></li>
        </ul>

        <h3>Spróbuj wszystkiego na {BRAND}</h3>

        <p>Platforma <a href={localePath('/')}>{BRAND}</a> oferuje powieści wszystkich gatunków:</p>

        <ul>
          <li><strong>Romans</strong> — stwórz swoją idealną historię miłości</li>
          <li><strong>Tajemnica</strong> — zbadaj unikalne sprawy</li>
          <li><strong>Horror</strong> — doświadcz prawdziwego strachu przed nieznanym</li>
          <li><strong>Fantasy</strong> — zostań bohaterem magicznego świata</li>
        </ul>

        <p>I co najważniejsze — każda historia jest unikalna dzięki AI.</p>

        <h2>Podsumowanie</h2>

        <p>Powieści wizualne obejmują wszystkie możliwe gatunki — od lekkiego romansu do mrocznego horroru. Niezależnie od tego, jakie historie lubisz, znajdziesz idealną powieść.</p>

        <p>A jeśli chcesz spróbować wszystkich gatunków z maksymalną swobodą — <a href={localePath('/')}>{BRAND}</a> na Ciebie czeka. Utwórz postać, wybierz świat i przeżyj historię, która będzie tylko Twoja.</p>

        <p><strong>Jaki gatunek wybierzesz?</strong></p>
      </>
    ),
  },

  portuguese: {
    title: '🎭 Gêneros de visual novel: romance, mistério, terror',
    description: 'As visual novels são um gênero multifacetado, com histórias para todos os gostos. Vamos conhecer os principais gêneros.',
    seoTitle: `Gêneros de visual novel: romance, mistério, terror — ${BRAND}`,
    seoDescription: 'As visual novels são um gênero multifacetado, com histórias para todos os gostos. Do romance emocionante ao terror arrepiante — conheça os principais gêneros com exemplos.',
    content: (
      <>
        <p>As visual novels são um gênero multifacetado, com histórias para todos os gostos. De dramas românticos emocionantes a terrores de arrepiar — todo mundo encontra algo de que gosta. Neste artigo, vamos conhecer os principais gêneros de visual novel, com exemplos e características.</p>

        <h2>Romance</h2>

        <h3>O que é?</h3>

        <p>As visual novels românticas (Romance VN) são o gênero mais popular, em que a história se concentra no desenvolvimento de relacionamentos amorosos.</p>

        <h3>Subgêneros do romance</h3>

        <p><strong>Otome (Otome Game)</strong></p>
        <ul>
          <li>Voltado para o público feminino</li>
          <li>A protagonista escolhe entre vários personagens masculinos</li>
          <li>Exemplos: Mystic Messenger, Amnesia, Hakuoki</li>
        </ul>

        <p><strong>Galge (Galge/Bishoujo)</strong></p>
        <ul>
          <li>Voltado para o público masculino</li>
          <li>O protagonista desenvolve relacionamentos com personagens femininas</li>
          <li>Exemplos: Clannad, Katawa Shoujo, Doki Doki Literature Club</li>
        </ul>

        <p><strong>BL (Boys&apos; Love/Yaoi)</strong></p>
        <ul>
          <li>Romance entre personagens masculinos</li>
          <li>Exemplos: Dramatical Murder, No, Thank You!!!</li>
        </ul>

        <p><strong>GL (Girls&apos; Love/Yuri)</strong></p>
        <ul>
          <li>Romance entre personagens femininas</li>
          <li>Exemplos: Kindred Spirits on the Roof, Highway Blossoms</li>
        </ul>

        <h3>Características do gênero</h3>

        <ul>
          <li><strong>Rotas de personagens</strong> — ramificações separadas da história para cada interesse romântico</li>
          <li><strong>Sistema de afeto</strong> — as escolhas afetam os relacionamentos</li>
          <li><strong>Vários finais</strong> — do feliz ao trágico</li>
          <li><strong>Momentos-chave</strong> — declarações, primeiro beijo, conflitos</li>
        </ul>

        <h3>Arquétipos de personagens</h3>

        <table>
          <thead>
            <tr>
              <th>Arquétipo</th>
              <th>Características</th>
              <th>Exemplo</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Tsundere</td>
              <td>Fria por fora, carinhosa por dentro</td>
              <td>Hostilidade → afeto</td>
            </tr>
            <tr>
              <td>Kuudere</td>
              <td>Calma, sem emoções aparentes</td>
              <td>Vai se abrindo emocionalmente aos poucos</td>
            </tr>
            <tr>
              <td>Yandere</td>
              <td>Amor obsessivo</td>
              <td>Doce → perigosa</td>
            </tr>
            <tr>
              <td>Dandere</td>
              <td>Tímida, quieta</td>
              <td>Vai se soltando aos poucos</td>
            </tr>
            <tr>
              <td>Genki</td>
              <td>Enérgica, alegre</td>
              <td>Transborda positividade</td>
            </tr>
          </tbody>
        </table>

        <h3>Romance na {BRAND}</h3>

        <p>Na plataforma <a href={localePath('/')}>{BRAND}</a>, você pode criar sua própria história romântica:</p>

        <ul>
          <li>Crie um personagem com a sua personalidade</li>
          <li>Desenvolva relacionamentos com as suas próprias palavras</li>
          <li>Cada conversa é única graças à IA</li>
          <li>Flerte, se declare, discuta — igualzinho à vida real</li>
        </ul>

        <h2>Mistério e misticismo</h2>

        <h3>O que é?</h3>

        <p>As visual novels de mistério (Mystery VN) são histórias em que o jogador investiga crimes, resolve enigmas ou desvenda segredos.</p>

        <h3>Subgêneros</h3>

        <p><strong>Detetive clássico</strong></p>
        <ul>
          <li>Investigação de assassinatos</li>
          <li>Coleta de provas e interrogatórios</li>
          <li>Deduções lógicas</li>
          <li>Exemplos: Ace Attorney, Danganronpa</li>
        </ul>

        <p><strong>Thriller místico</strong></p>
        <ul>
          <li>Elementos sobrenaturais</li>
          <li>Investigações paranormais</li>
          <li>Exemplos: Umineko no Naku Koro ni, Higurashi</li>
        </ul>

        <p><strong>Thriller psicológico</strong></p>
        <ul>
          <li>Jogos mentais</li>
          <li>Narrador não confiável</li>
          <li>Exemplos: Ever17, Remember11</li>
        </ul>

        <p><strong>Noir</strong></p>
        <ul>
          <li>Atmosfera sombria</li>
          <li>Protagonista cínico</li>
          <li>Submundo do crime</li>
        </ul>

        <h3>Características do gênero</h3>

        <ul>
          <li><strong>Sistema de provas</strong> — o jogador reúne evidências</li>
          <li><strong>Interrogatórios</strong> — contradições nos depoimentos</li>
          <li><strong>Cadeias lógicas</strong> — ligar os fatos</li>
          <li><strong>Reviravoltas</strong> — mudanças inesperadas</li>
          <li><strong>Vários suspeitos</strong> — quem é o verdadeiro culpado?</li>
        </ul>

        <h3>Elementos de um bom mistério</h3>

        <ol>
          <li><strong>Jogo limpo</strong> — todas as pistas são mostradas ao jogador antes da solução</li>
          <li><strong>Motivação do vilão</strong> — razões compreensíveis para o crime</li>
          <li><strong>Pistas falsas</strong> — manobras para despistar</li>
          <li><strong>Solução satisfatória</strong> — uma resposta lógica e inesperada</li>
        </ol>

        <h3>Mistérios na {BRAND}</h3>

        <p>A <a href={localePath('/')}>{BRAND}</a> permite que você investigue casos únicos:</p>

        <ul>
          <li>A IA cria enigmas e se adapta às suas ações</li>
          <li>Interrogue os suspeitos com as suas próprias perguntas</li>
          <li>Cada investigação é única</li>
          <li>Torne-se detetive num mundo sem respostas predefinidas</li>
          <li>Experimente a novel <a href={localePath('/production/_labirint_pamyati')}>Labirinto da Memória</a></li>
        </ul>

        <h2>Terror</h2>

        <h3>O que é?</h3>

        <p>As novels de terror (Horror VN) são histórias criadas para assustar, causar angústia e deixar você com os nervos à flor da pele.</p>

        <h3>Subgêneros</h3>

        <p><strong>Terror psicológico</strong></p>
        <ul>
          <li>Medo por meio da atmosfera e do desconhecido</li>
          <li>Jogos com a percepção da realidade</li>
          <li>Exemplos: Doki Doki Literature Club, Saya no Uta</li>
        </ul>

        <p><strong>Terror sobrenatural</strong></p>
        <ul>
          <li>Fantasmas, demônios, maldições</li>
          <li>A mitologia japonesa aparece com frequência</li>
          <li>Exemplos: Corpse Party, Higurashi</li>
        </ul>

        <p><strong>Survival Horror</strong></p>
        <ul>
          <li>Sobrevivência em situações perigosas</li>
          <li>Recursos limitados</li>
          <li>Monstros e perseguidores</li>
        </ul>

        <p><strong>Gótico</strong></p>
        <ul>
          <li>Castelos e mansões sombrios</li>
          <li>Segredos de família</li>
          <li>Atmosfera de decadência</li>
        </ul>

        <h3>Características do gênero</h3>

        <ul>
          <li><strong>Atmosfera</strong> — música, visual e texto criam angústia</li>
          <li><strong>Jump scares</strong> — sustos repentinos</li>
          <li><strong>Construção lenta</strong> — a tensão cresce aos poucos</li>
          <li><strong>Finais ruins</strong> — a morte do herói é um desfecho comum</li>
          <li><strong>Mistérios</strong> — o que está acontecendo de verdade?</li>
        </ul>

        <h3>Técnicas para criar medo</h3>

        <table>
          <thead>
            <tr>
              <th>Técnica</th>
              <th>Descrição</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>O desconhecido</td>
              <td>A ameaça não é mostrada diretamente</td>
            </tr>
            <tr>
              <td>Isolamento</td>
              <td>O herói está sozinho, sem ajuda</td>
            </tr>
            <tr>
              <td>Impotência</td>
              <td>Só dá para fugir, não para lutar</td>
            </tr>
            <tr>
              <td>Vale da estranheza</td>
              <td>Algo familiar, mas &quot;errado&quot;</td>
            </tr>
            <tr>
              <td>Silêncio</td>
              <td>A pausa antes do horror</td>
            </tr>
            <tr>
              <td>Expectativas subvertidas</td>
              <td>O que era seguro se torna perigoso</td>
            </tr>
          </tbody>
        </table>

        <h3>Terror na {BRAND}</h3>

        <p>Na <a href={localePath('/')}>{BRAND}</a>, as histórias de terror são especialmente eficazes:</p>

        <ul>
          <li>A IA é imprevisível — você não sabe o que vem a seguir</li>
          <li>Os personagens reagem ao seu medo</li>
          <li>Cada partida traz novos horrores</li>
          <li>Uma verdadeira sensação de desconhecido</li>
          <li>Experimente a novel <a href={localePath('/production/lastnightgame')}>A Última Noite</a></li>
        </ul>

        <h2>Fantasia</h2>

        <h3>O que é?</h3>

        <p>As novels de fantasia levam o jogador a mundos de magia, espadas e aventuras épicas.</p>

        <h3>Subgêneros</h3>

        <ul>
          <li><strong>Fantasia clássica</strong> — elfos, dragões, missões</li>
          <li><strong>Dark fantasy</strong> — mundo sombrio, ambiguidade moral</li>
          <li><strong>Fantasia urbana</strong> — magia no mundo moderno</li>
          <li><strong>Fantasia oriental</strong> — mitologia asiática</li>
          <li><strong>LitRPG</strong> — mecânicas de jogo na trama</li>
        </ul>

        <h3>Características do gênero</h3>

        <ul>
          <li><strong>Sistema de magia</strong> — como a magia funciona</li>
          <li><strong>Worldbuilding</strong> — um mundo cuidadosamente construído</li>
          <li><strong>Escala épica</strong> — o destino do mundo em jogo</li>
          <li><strong>Aventuras</strong> — viagens e batalhas</li>
        </ul>

        <h2>Slice of Life</h2>

        <h3>O que é?</h3>

        <p>Slice of Life são histórias sobre a vida comum, sem conflitos globais.</p>

        <h3>Características</h3>

        <ul>
          <li>Escola, trabalho, amizade</li>
          <li>Sem antagonista nem catástrofe</li>
          <li>Foco nas relações entre as pessoas</li>
          <li>Humor e momentos emocionantes</li>
          <li>Exemplos: Endless Summer, Clannad (em parte)</li>
        </ul>

        <h2>Ficção científica</h2>

        <h3>O que é?</h3>

        <p>As novels de ficção científica exploram o impacto da tecnologia na sociedade e na humanidade.</p>

        <h3>Subgêneros</h3>

        <ul>
          <li><strong>Cyberpunk</strong> — alta tecnologia, baixa qualidade de vida</li>
          <li><strong>Space opera</strong> — aventuras no espaço</li>
          <li><strong>Pós-apocalipse</strong> — o mundo depois da catástrofe</li>
          <li><strong>Loops temporais</strong> — viagens no tempo</li>
        </ul>

        <h3>Exemplos</h3>

        <ul>
          <li>Steins;Gate — viagem no tempo</li>
          <li>AI: The Somnium Files — tecnologia dos sonhos</li>
          <li>Planetarian — um robô e o fim do mundo</li>
        </ul>

        <h2>Gêneros mistos</h2>

        <p>As melhores novels muitas vezes combinam gêneros:</p>

        <table>
          <thead>
            <tr>
              <th>Novel</th>
              <th>Gêneros</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Doki Doki Literature Club</td>
              <td>Romance + Terror psicológico</td>
            </tr>
            <tr>
              <td>Danganronpa</td>
              <td>Mistério + Terror + Thriller</td>
            </tr>
            <tr>
              <td>Steins;Gate</td>
              <td>Ficção científica + Thriller + Romance</td>
            </tr>
            <tr>
              <td>Fate/stay night</td>
              <td>Fantasia + Ação + Romance</td>
            </tr>
            <tr>
              <td>Umineko</td>
              <td>Mistério + Misticismo + Fantasia</td>
            </tr>
          </tbody>
        </table>

        <h2>Como escolher o gênero ideal para você</h2>

        <h3>Quiz: qual gênero combina com você?</h3>

        <p><strong>O que mais importa para você numa história?</strong></p>
        <ul>
          <li>Relações entre os personagens → <strong>Romance</strong></li>
          <li>Enigmas e lógica → <strong>Mistério</strong></li>
          <li>Emoções e atmosfera → <strong>Terror</strong> ou <strong>Drama</strong></li>
          <li>Aventura e ação → <strong>Fantasia</strong></li>
          <li>Ideias e conceitos → <strong>Ficção científica</strong></li>
        </ul>

        <h3>Experimente tudo na {BRAND}</h3>

        <p>A plataforma <a href={localePath('/')}>{BRAND}</a> oferece novels de todos os gêneros:</p>

        <ul>
          <li><strong>Romance</strong> — crie a sua história de amor ideal</li>
          <li><strong>Mistério</strong> — investigue casos únicos</li>
          <li><strong>Terror</strong> — sinta o verdadeiro medo do desconhecido</li>
          <li><strong>Fantasia</strong> — torne-se o herói de um mundo mágico</li>
        </ul>

        <p>E o mais importante: cada história é única graças à IA.</p>

        <h2>Conclusão</h2>

        <p>As visual novels abrangem todos os gêneros possíveis — do romance leve ao terror sombrio. Seja qual for o tipo de história de que você gosta, você vai encontrar a novel perfeita.</p>

        <p>E, se você quiser experimentar todos os gêneros com o máximo de liberdade, a <a href={localePath('/')}>{BRAND}</a> está esperando por você. Crie um personagem, escolha um mundo e viva uma história que será só sua.</p>

        <p><strong>Qual gênero você vai escolher?</strong></p>
      </>
    ),
  },
};

export default post;
